import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const API_BASE = (axiosInstance.defaults.baseURL ?? '').replace(/\/$/, '');

interface Daire {
    daireID: number;
    daireNo: string;
    blokID: number;
    katID: number;
    cepheID: number;
    manzaraID: number;
    daireTipID: number;
    daireDurumu: string;
    listeFiyati?: number;
    netM2?: number;
    brutM2?: number;
    aktifMi: boolean;
    takipNo?: string;
    proje?: { takipNo?: string };
}

interface SunumBelge {
    sunumBelgeId: number;
    takipNo: string;
    aciklama: string | null;
    sunumBelgeTuru: string;
    dosyaAdi: string | null;
}

interface Gorunum {
    gorunumID: number;
    projeID: number;
    gorunumAdi: string;
    aciklama?: string;
    arkaplanGorsel?: string;
    gorunumTipi: string; // '2D' | '3D'
    siraNo: number;
    daireler: GorunumDaire[];
    cepheler: Cephe[];
}

interface Cephe {
    cepheID: number;
    gorunumID: number;
    cepheAdi: string;
    gorselYolu?: string;
    siraNo: number;
}

interface GorunumDaire {
    gorunumDaireID: number;
    gorunumID: number;
    cepheID?: number | null;
    daireID: number;
    xYuzde: number;
    yYuzde: number;
    rxMin?: number | null;
    ryMin?: number | null;
    rxMax?: number | null;
    ryMax?: number | null;
    daire?: Daire;
}

type Placement = { x: number; y: number; rx1?: number; ry1?: number; rx2?: number; ry2?: number; };

// "Alan Çiz" ile çizilmiş dikdörtgeni sonradan düzeltmek için: köşe/kenar tutamaçları
// ve gövdeden taşıma. Her tutamaç türü, sürüklendiğinde hangi sınır(lar)ı değiştireceğini belirtir.
type RectHandleKind = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
const RECT_HANDLE_EFFECTS: Record<RectHandleKind, { x?: 'rx1' | 'rx2'; y?: 'ry1' | 'ry2' }> = {
    nw: { x: 'rx1', y: 'ry1' }, n: { y: 'ry1' }, ne: { x: 'rx2', y: 'ry1' }, e: { x: 'rx2' },
    se: { x: 'rx2', y: 'ry2' }, s: { y: 'ry2' }, sw: { x: 'rx1', y: 'ry2' }, w: { x: 'rx1' },
};
const RECT_HANDLE_CURSOR: Record<RectHandleKind, string> = {
    nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize',
    n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
};
const RECT_MIN_SIZE = 1; // % — köşe/kenar sürüklenirken karşı kenarı geçip ters dönmesini engeller
const DOT_SNAP_PX = 8; // px — bir nokta sürüklenirken diğer noktalarla bu ekran mesafesi içindeyse hizalanır
type RectDragState = {
    daireID: number;
    kind: RectHandleKind | 'move';
    startPos: { x: number; y: number };
    startRect: { rx1: number; ry1: number; rx2: number; ry2: number };
};

interface LookupItem { id: number; ad: string; }

interface SunumListeItem {
    sunumID: number;
    takipNo: string;
    cariAdSoyad: string | null;
    cariFirma: string | null;
    baslik: string | null;
    durum: string;
    daireler: { daireID: number }[];
}

interface Props {
    projeID: number;
    daireler: Daire[];
    bloklar: LookupItem[];
    katlar: LookupItem[];
    cepheler: LookupItem[];
    manzaralar: LookupItem[];
    /** Daire tipi adlarının (2+1, 3+1 …) tooltip ve detay modalinde gösterilebilmesi için. */
    daireTipleri?: LookupItem[];
    onClose: () => void;
    readOnly?: boolean;
    /** Verilirse ve görünümler arasında bulunursa, açılışta bu görünüm seçili gelir (aksi halde ilk görünüm). */
    initialGorunumID?: number;
}

const durumMeta: Record<string, { bg: string; border: string; text: string }> = {
    'Satışta': { bg: '#22c55e', border: '#16a34a', text: '#fff' },
    'Rezerve': { bg: '#f59e0b', border: '#d97706', text: '#fff' },
    'Sözleşme': { bg: '#3b82f6', border: '#2563eb', text: '#fff' },
};

const formatMoney = (n?: number) =>
    n !== undefined && n !== null ? n.toLocaleString('tr-TR') : '—';

const ALL_DURUM_KEYS = ['Satışta', 'Rezerve', 'Sözleşme', 'Diğer'];
const DURUM_FILTER_COOKIE = 'pgv_visible_durumlar';
// Tek harfli kodlarla saklanır (JSON/tırnak değil) — WAF'ların (ör. ModSecurity SQLi kuralı 942200)
// virgül+tırnak dizilerini enjeksiyon sanıp tüm siteyi 403'lemesini önlemek için.
const DURUM_CODE: Record<string, string> = { 'Satışta': 'S', 'Rezerve': 'R', 'Sözleşme': 'Z', 'Diğer': 'D' };
const CODE_DURUM: Record<string, string> = { S: 'Satışta', R: 'Rezerve', Z: 'Sözleşme', D: 'Diğer' };

const getDurumKey = (daireDurumu: string) => (durumMeta[daireDurumu] ? daireDurumu : 'Diğer');

const readCookie = (name: string): string | null => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name: string, value: string) => {
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${365 * 24 * 60 * 60}`;
};

const loadVisibleDurumlar = (): Set<string> => {
    const raw = readCookie(DURUM_FILTER_COOKIE);
    if (raw) {
        const durumlar = raw.split('-').map(code => CODE_DURUM[code]).filter(Boolean);
        if (durumlar.length > 0) return new Set(durumlar);
    }
    return new Set(ALL_DURUM_KEYS);
};

// Kaydırma miktarını, görsel tamamen ekran dışına çıkmayacak şekilde sınırlar.
const clampPan = (px: number, py: number, z: number) => ({
    x: Math.max(-100, Math.min(100 / z, px)),
    y: Math.max(-100, Math.min(100 / z, py)),
});

// Yerleştirilmiş noktaların (ve varsa dikdörtgenlerin) sınır kutusuna ~%15 payla otomatik yakınlaşma.
const AUTO_FIT_MARGIN = 0.15;
const AUTO_FIT_MIN_SPAN = 18; // % — tek nokta ya da çok yakın noktalarda aşırı yakınlaşmayı önler
const AUTO_FIT_MAX_ZOOM = 6;
const computeFitForPlacements = (placements: Record<number, Placement>): { zoom: number; panX: number; panY: number } => {
    const xs: number[] = [];
    const ys: number[] = [];
    Object.values(placements).forEach(pos => {
        xs.push(pos.x); ys.push(pos.y);
        if (pos.rx1 !== undefined) xs.push(pos.rx1);
        if (pos.rx2 !== undefined) xs.push(pos.rx2);
        if (pos.ry1 !== undefined) ys.push(pos.ry1);
        if (pos.ry2 !== undefined) ys.push(pos.ry2);
    });
    if (xs.length === 0) return { zoom: 1, panX: 0, panY: 0 };
    const x1 = Math.min(...xs), x2 = Math.max(...xs);
    const y1 = Math.min(...ys), y2 = Math.max(...ys);
    const rawW = Math.max(x2 - x1, AUTO_FIT_MIN_SPAN);
    const rawH = Math.max(y2 - y1, AUTO_FIT_MIN_SPAN);
    const viewW = Math.min(100, rawW / (1 - 2 * AUTO_FIT_MARGIN));
    const viewH = Math.min(100, rawH / (1 - 2 * AUTO_FIT_MARGIN));
    const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2;
    const newZoom = Math.min(AUTO_FIT_MAX_ZOOM, Math.max(1, Math.min(100 / viewW, 100 / viewH)));
    const pan = clampPan(50 / newZoom - cx, 50 / newZoom - cy, newZoom);
    return { zoom: newZoom, panX: pan.x, panY: pan.y };
};

const hexToRgba = (hex: string, alpha: number): string => {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const n = parseInt(full, 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

// ── Etiket (daire no) yerleşimi ─────────────────────────────────────────────
// Noktalar birbirine yakınsa etiketleri (tam daire no metnini) çakışmadan
// göstermek için basit bir "greedy" algoritma: her etiket için önce noktanın
// çevresinde saat yönünde birkaç açı/mesafe adayı dener, ilk çakışmasız
// konuma yerleşir; hiçbiri boşsa en uzak halkada (üstte) çakışma kabul edilir.
let _labelMeasureCtx: CanvasRenderingContext2D | null | undefined;
const getLabelMeasureCtx = (): CanvasRenderingContext2D | null => {
    if (_labelMeasureCtx === undefined) {
        _labelMeasureCtx = document.createElement('canvas').getContext('2d');
    }
    return _labelMeasureCtx;
};
const LABEL_FONT = "700 10.5px 'Inter',sans-serif";
const LABEL_H = 18;
const LABEL_PAD_X = 7;
const LABEL_GAP = 6;
const measureLabelTextWidth = (text: string): number => {
    const ctx = getLabelMeasureCtx();
    if (!ctx) return text.length * 6.5 + 4;
    ctx.font = LABEL_FONT;
    return ctx.measureText(text).width;
};

interface LabelMarkerInput { daireID: number; ax: number; ay: number; text: string; }
interface LabelPlacementResult { daireID: number; x: number; y: number; w: number; h: number; needsConnector: boolean; }

const computeLabelLayout = (markers: LabelMarkerInput[], dotRadiusPx: number): LabelPlacementResult[] => {
    const placedRects: { x: number; y: number; w: number; h: number }[] = [];
    const results: LabelPlacementResult[] = [];
    const sorted = [...markers].sort((a, b) => (a.ay - b.ay) || (a.ax - b.ax));
    const overlaps = (a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }) =>
        a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
    const baseDist = dotRadiusPx + LABEL_GAP + LABEL_H / 2;
    const angles = [-90, -45, -135, 0, 180, 45, 135, 90];
    const rings = [1, 1.9, 2.8];

    sorted.forEach(m => {
        const w = measureLabelTextWidth(m.text) + LABEL_PAD_X * 2;
        const h = LABEL_H;
        let center: { x: number; y: number } | null = null;
        ringLoop:
        for (const ring of rings) {
            for (const ang of angles) {
                const rad = (ang * Math.PI) / 180;
                const dist = baseDist * ring;
                const cx = m.ax + Math.cos(rad) * dist;
                const cy = m.ay + Math.sin(rad) * dist;
                const rect = { x: cx - w / 2, y: cy - h / 2, w, h };
                const hitsLabel = placedRects.some(p => overlaps(rect, p));
                const hitsOtherDot = sorted.some(o => o.daireID !== m.daireID &&
                    o.ax > rect.x && o.ax < rect.x + rect.w && o.ay > rect.y && o.ay < rect.y + rect.h);
                if (!hitsLabel && !hitsOtherDot) { center = { x: cx, y: cy }; break ringLoop; }
            }
        }
        if (!center) {
            center = { x: m.ax, y: m.ay - baseDist * rings[rings.length - 1] * 1.3 };
        }
        const rect = { x: center.x - w / 2, y: center.y - h / 2, w, h };
        placedRects.push(rect);
        const dist = Math.hypot(center.x - m.ax, center.y - m.ay);
        results.push({ daireID: m.daireID, x: rect.x, y: rect.y, w, h, needsConnector: dist > baseDist * 1.05 });
    });
    return results;
};

const ProjeGorunumleri: React.FC<Props> = ({ projeID, daireler, bloklar, katlar, cepheler, manzaralar, daireTipleri = [], onClose, readOnly = false, initialGorunumID }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [gorunumler, setGorunumler] = useState<Gorunum[]>([]);
    const [selectedID, setSelectedID] = useState<number | null>(null);
    const [placements, setPlacements] = useState<Record<number, Placement>>({});
    const [showForm, setShowForm] = useState(false);
    const [editingGorunum, setEditingGorunum] = useState<Gorunum | null>(null);
    const [formAdi, setFormAdi] = useState('');
    const [formAciklama, setFormAciklama] = useState('');
    const [formGorunumTipi, setFormGorunumTipi] = useState<'2D' | '3D'>('2D');
    const [saving, setSaving] = useState(false);
    const [gorselUploading, setGorselUploading] = useState(false);
    // 3D Görünüm — cephe (fotoğraf) yönetimi
    const [activeCepheID, setActiveCepheID] = useState<number | null>(null);
    const [showCepheForm, setShowCepheForm] = useState(false);
    const [editingCephe, setEditingCephe] = useState<Cephe | null>(null);
    const [cepheFormAdi, setCepheFormAdi] = useState('');
    const [cepheSaving, setCepheSaving] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [visibleDurumlar, setVisibleDurumlar] = useState<Set<string>>(() => loadVisibleDurumlar());
    const [hoveredDaireID, setHoveredDaireID] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
    // Tam ekran: kenar panelleri gizlenir, 4:3 tuval tüm ekranı kaplar
    const [tamEkran, setTamEkran] = useState(false);
    const [filterPanel, setFilterPanel] = useState('');
    const [filterPanelBlok, setFilterPanelBlok] = useState<number | ''>('');
    const [filterPanelKat, setFilterPanelKat] = useState<number | ''>('');
    const [draggingDaireID, setDraggingDaireID] = useState<number | null>(null);
    const [draggingOnCanvas, setDraggingOnCanvas] = useState<number | null>(null);
    const [dragOffset, setDragOffset] = useState<{ ox: number; oy: number } | null>(null);
    // Bir nokta sürüklenirken diğer yerleştirilmiş noktalarla aynı hizaya (x veya y) geldiğinde
    // gösterilen hizalama çizgisi — değerler % cinsinden, o hizadaki diğer noktanın konumu.
    const [alignGuide, setAlignGuide] = useState<{ x: number | null; y: number | null }>({ x: null, y: null });
    const [activeEfekt, setActiveEfekt] = useState<string>('normal');
    const [rectMode, setRectMode] = useState(false);
    const [selectedDaireForRect, setSelectedDaireForRect] = useState<number | null>(null);
    const [rectDrawStart, setRectDrawStart] = useState<{ x: number; y: number } | null>(null);
    const [rectDrawCur, setRectDrawCur] = useState<{ x: number; y: number } | null>(null);
    // Var olan bir alanı köşe/kenar tutamaçlarıyla yeniden boyutlandırma ya da gövdesinden taşıma
    const [rectDrag, setRectDrag] = useState<RectDragState | null>(null);
    // Yakınlaştırma / kaydırma
    const [zoom, setZoom] = useState(1);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [zoomSelectMode, setZoomSelectMode] = useState(false);
    const [zoomSelectStart, setZoomSelectStart] = useState<{ x: number; y: number } | null>(null);
    const [zoomSelectCur, setZoomSelectCur] = useState<{ x: number; y: number } | null>(null);
    const [isPanning, setIsPanning] = useState(false);
    const panStartRef = useRef<{ clientX: number; clientY: number; panX: number; panY: number } | null>(null);
    // Daire detay & sunuma ekle
    const [detayDaire, setDetayDaire] = useState<Daire | null>(null);
    const [galeriGorseller, setGaleriGorseller] = useState<SunumBelge[]>([]);
    const [galeriSlide, setGaleriSlide] = useState(0);
    const [galeriYukleniyor, setGaleriYukleniyor] = useState(false);
    const [sunumModal, setSunumModal] = useState(false);
    const [sunumlar, setSunumlar] = useState<SunumListeItem[]>([]);
    const [sunumYukleniyor, setSunumYukleniyor] = useState(false);
    const [sunumEkleniyor, setSunumEkleniyor] = useState<number | null>(null);
    const [sunumEklendi, setSunumEklendi] = useState<Set<number>>(new Set());
    const mouseMovedRef = useRef(false);
    const [modalPos, setModalPos] = useState<{ x: number; y: number } | null>(null);
    const modalDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Tuvalin ekranda gerçekten kapladığı piksel boyutu — marker/etiket boyutu ve
    // etiket çakışma hesaplaması bu ölçüye göre yapılır (doğal görsel çözünürlüğüne değil).
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

    const cssEfektler: { key: string; label: string; icon: string; filter: string }[] = [
        { key: 'normal',    label: 'Normal',   icon: '☀️',  filter: 'none' },
        { key: 'aydinlik',  label: 'Aydınlık', icon: '🌤️', filter: 'brightness(1.25) saturate(1.1)' },
        { key: 'gunes',     label: 'Güneşli',  icon: '🌞',  filter: 'brightness(1.4) saturate(1.4) contrast(1.05) sepia(0.15)' },
        { key: 'bulutlu',   label: 'Bulutlu',  icon: '☁️',  filter: 'brightness(0.9) saturate(0.8) contrast(0.95)' },
        { key: 'karanlik',  label: 'Karanlık', icon: '🌑',  filter: 'brightness(0.55) saturate(0.7)' },
        { key: 'gece',      label: 'Gece',     icon: '🌃',  filter: 'brightness(0.35) saturate(0.5) hue-rotate(200deg)' },
        { key: 'gün_batımı',label: 'Gün Batımı','icon': '🌅', filter: 'brightness(1.05) sepia(0.45) saturate(1.6) hue-rotate(-15deg)' },
        { key: 'sisli',     label: 'Sisli',    icon: '🌫️',  filter: 'brightness(1.1) contrast(0.75) saturate(0.6) blur(1.2px)' },
        { key: 'bw',        label: 'Siyah-Beyaz','icon':'⬛', filter: 'grayscale(1)' },
        { key: 'retro',     label: 'Retro',    icon: '📷',  filter: 'sepia(0.6) contrast(1.1) brightness(0.95) saturate(0.8)' },
    ];

    const selectedGorunum = gorunumler.find(g => g.gorunumID === selectedID) ?? null;
    const is3D = selectedGorunum?.gorunumTipi === '3D';
    const cepheListSorted = selectedGorunum ? [...selectedGorunum.cepheler].sort((a, b) => a.siraNo - b.siraNo) : [];
    const activeCephe = is3D ? (cepheListSorted.find(c => c.cepheID === activeCepheID) ?? null) : null;
    const canvasGorsel = is3D ? activeCephe?.gorselYolu : selectedGorunum?.arkaplanGorsel;

    useEffect(() => { fetchGorunumler(); }, [projeID]);

    useEffect(() => {
        writeCookie(DURUM_FILTER_COOKIE, Array.from(visibleDurumlar).map(d => DURUM_CODE[d] ?? '').filter(Boolean).join('-'));
    }, [visibleDurumlar]);

    const toggleDurumVisible = (durum: string) => {
        setVisibleDurumlar(prev => {
            const next = new Set(prev);
            if (next.has(durum)) next.delete(durum); else next.add(durum);
            return next;
        });
    };

    // 3D ise ilk cepheyi seç ve — görsel yüklenmiş en az bir cephe varsa — doğrudan
    // Önizleme modunda aç (müşteriye sunum burada yapılacağı için varsayılan budur).
    const applyDefaultModeFor = (g: Gorunum | null) => {
        const g3d = g?.gorunumTipi === '3D';
        const sortedCepheler = g?.cepheler ? [...g.cepheler].sort((a, b) => a.siraNo - b.siraNo) : [];
        if (g3d) {
            setActiveCepheID(sortedCepheler[0]?.cepheID ?? null);
            setPreviewMode(sortedCepheler.some(c => c.gorselYolu));
        } else {
            setActiveCepheID(null);
            setPreviewMode(false);
        }
        setPreviewIndex(0);
        setRectMode(false);
        setSelectedDaireForRect(null);
    };

    const selectGorunum = (gorunumID: number) => {
        setSelectedID(gorunumID);
        applyDefaultModeFor(gorunumler.find(x => x.gorunumID === gorunumID) ?? null);
    };

    // Aktif cephe silinmiş/geçersiz kalırsa listedeki ilk cepheye düş
    useEffect(() => {
        if (is3D && activeCepheID !== null && !cepheListSorted.some(c => c.cepheID === activeCepheID)) {
            setActiveCepheID(cepheListSorted[0]?.cepheID ?? null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [is3D, cepheListSorted]);

    useEffect(() => {
        const kaynakDaireler = is3D
            ? (selectedGorunum?.daireler.filter(d => d.cepheID === activeCepheID) ?? [])
            : (selectedGorunum?.daireler ?? []);
        const p: Record<number, Placement> = {};
        kaynakDaireler.forEach(d => {
            p[d.daireID] = {
                x: d.xYuzde, y: d.yYuzde,
                rx1: d.rxMin ?? undefined, ry1: d.ryMin ?? undefined,
                rx2: d.rxMax ?? undefined, ry2: d.ryMax ?? undefined,
            };
        });
        setPlacements(p);
        setActiveEfekt('normal');
        setRectMode(false);
        setSelectedDaireForRect(null);
        // Otomatik odaklanma: yerleştirilmiş noktaların (ve dikdörtgenlerin) sınır kutusuna
        // ~%15 payla yakınlaşır; hiç yerleşim yoksa tüm fotoğraf gösterilir (zoom=1).
        const fit = computeFitForPlacements(p);
        setZoom(fit.zoom);
        setPanX(fit.panX);
        setPanY(fit.panY);
        setZoomSelectMode(false);
        setZoomSelectStart(null);
        setZoomSelectCur(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedID, activeCepheID, gorunumler]);

    const fetchGorunumler = async () => {
        try {
            const res = await axiosInstance.get(`/api/ProjeGorunumleri?projeID=${projeID}`);
            setGorunumler(res.data);
            if (res.data.length > 0 && selectedID === null) {
                const initial = initialGorunumID != null ? res.data.find((g: Gorunum) => g.gorunumID === initialGorunumID) : null;
                const secilecek = initial ?? res.data[0];
                setSelectedID(secilecek.gorunumID);
                applyDefaultModeFor(secilecek);
            }
        } catch { toast.error(t('msg.islemHatasi')); }
    };

    const handleSaveForm = async () => {
        if (!formAdi.trim()) { toast.error(t('msg.islemHatasi')); return; }
        setSaving(true);
        try {
            if (editingGorunum) {
                await axiosInstance.post(`/api/ProjeGorunumleri/update/${editingGorunum.gorunumID}`, {
                    gorunumAdi: formAdi, aciklama: formAciklama, siraNo: editingGorunum.siraNo
                });
                toast.success(t('msg.islemBasarili'));
            } else {
                const res = await axiosInstance.post('/api/ProjeGorunumleri', {
                    projeID, gorunumAdi: formAdi, aciklama: formAciklama, siraNo: gorunumler.length + 1,
                    gorunumTipi: formGorunumTipi
                });
                setSelectedID(res.data.gorunumID);
                applyDefaultModeFor(res.data);
                toast.success(t('msg.islemBasarili'));
            }
            setShowForm(false);
            await fetchGorunumler();
        } catch { toast.error(t('msg.islemHatasi')); }
        setSaving(false);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/ProjeGorunumleri/${id}`);
            toast.success(t('msg.islemBasarili'));
            if (selectedID === id) setSelectedID(null);
            await fetchGorunumler();
        } catch { toast.error(t('msg.islemHatasi')); }
    };

    const handleGorselUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!selectedID || !e.target.files?.[0]) return;
        setGorselUploading(true);
        const formData = new FormData();
        formData.append('file', e.target.files[0]);
        try {
            if (is3D) {
                if (!activeCepheID) { toast.error('Önce bir cephe seçin.'); setGorselUploading(false); e.target.value = ''; return; }
                await axiosInstance.post(`/api/ProjeGorunumleri/cephe/${activeCepheID}/gorsel`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                await axiosInstance.post(`/api/ProjeGorunumleri/${selectedID}/gorsel`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            toast.success(t('msg.islemBasarili'));
            await fetchGorunumler();
        } catch { toast.error(t('msg.islemHatasi')); }
        setGorselUploading(false);
        e.target.value = '';
    };

    const savePlacements = async () => {
        if (!selectedID) return;
        if (is3D && !activeCepheID) { toast.error('Önce bir cephe seçin.'); return; }
        setSaving(true);
        try {
            const payload = Object.entries(placements).map(([daireID, pos]) => ({
                daireID: Number(daireID), xYuzde: pos.x, yYuzde: pos.y,
                rxMin: pos.rx1 ?? null, ryMin: pos.ry1 ?? null,
                rxMax: pos.rx2 ?? null, ryMax: pos.ry2 ?? null,
            }));
            if (is3D) {
                await axiosInstance.post(`/api/ProjeGorunumleri/cephe/${activeCepheID}/daireler`, payload);
            } else {
                await axiosInstance.post(`/api/ProjeGorunumleri/${selectedID}/daireler`, payload);
            }
            toast.success(t('msg.islemBasarili'));
            await fetchGorunumler();
        } catch { toast.error(t('msg.islemHatasi')); }
        setSaving(false);
    };

    // ── 3D Görünüm: Cephe (fotoğraf) CRUD ──────────────────────────────────
    const handleAddCephe = async () => {
        if (!selectedID || !cepheFormAdi.trim()) return;
        setCepheSaving(true);
        try {
            const res = await axiosInstance.post(`/api/ProjeGorunumleri/${selectedID}/cephe`, {
                cepheAdi: cepheFormAdi, siraNo: cepheListSorted.length + 1
            });
            setActiveCepheID(res.data.cepheID);
            setCepheFormAdi('');
            setShowCepheForm(false);
            setEditingCephe(null);
            await fetchGorunumler();
        } catch { toast.error(t('msg.islemHatasi')); }
        setCepheSaving(false);
    };

    const handleRenameCephe = async () => {
        if (!editingCephe || !cepheFormAdi.trim()) return;
        setCepheSaving(true);
        try {
            await axiosInstance.post(`/api/ProjeGorunumleri/cephe/${editingCephe.cepheID}/update`, {
                cepheAdi: cepheFormAdi, siraNo: editingCephe.siraNo
            });
            setCepheFormAdi('');
            setShowCepheForm(false);
            setEditingCephe(null);
            await fetchGorunumler();
        } catch { toast.error(t('msg.islemHatasi')); }
        setCepheSaving(false);
    };

    const handleDeleteCephe = async (cepheID: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/ProjeGorunumleri/cephe/${cepheID}`);
            if (activeCepheID === cepheID) setActiveCepheID(null);
            await fetchGorunumler();
        } catch { toast.error(t('msg.islemHatasi')); }
    };

    const handleMoveCephe = async (cephe: Cephe, direction: -1 | 1) => {
        const idx = cepheListSorted.findIndex(c => c.cepheID === cephe.cepheID);
        const swapIdx = idx + direction;
        if (idx < 0 || swapIdx < 0 || swapIdx >= cepheListSorted.length) return;
        const other = cepheListSorted[swapIdx];
        try {
            await Promise.all([
                axiosInstance.post(`/api/ProjeGorunumleri/cephe/${cephe.cepheID}/update`, { cepheAdi: cephe.cepheAdi, siraNo: other.siraNo }),
                axiosInstance.post(`/api/ProjeGorunumleri/cephe/${other.cepheID}/update`, { cepheAdi: other.cepheAdi, siraNo: cephe.siraNo }),
            ]);
            await fetchGorunumler();
        } catch { toast.error(t('msg.islemHatasi')); }
    };


    // --- Canvas drop (from panel) ---
    // Ekran (client) koordinatını, güncel yakınlaştırma/kaydırmadan bağımsız olarak
    // her zaman görselin ORİJİNAL (0-100) yüzde koordinatına çevirir.
    const getCanvasPercent = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
        const el = canvasRef.current;
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const screenXPct = ((clientX - rect.left) / rect.width) * 100;
        const screenYPct = ((clientY - rect.top) / rect.height) * 100;
        const x = screenXPct / zoom - panX;
        const y = screenYPct / zoom - panY;
        return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
    }, [zoom, panX, panY]);

    // Tuvalin render edilen piksel boyutunu izler (pencere/tam-ekran değişince günceller)
    useEffect(() => {
        const el = canvasRef.current;
        if (!el) return;
        const measure = () => setCanvasSize({ width: el.clientWidth, height: el.clientHeight });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [selectedGorunum, tamEkran]);

    // Escape key exits rect/zoom modes
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setRectMode(false); setSelectedDaireForRect(null); setRectDrawStart(null); setRectDrawCur(null);
                setRectDrag(null);
                setZoomSelectMode(false); setZoomSelectStart(null); setZoomSelectCur(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const handleCanvasDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const id = Number(e.dataTransfer.getData('daireID'));
        if (!id) return;
        const pos = getCanvasPercent(e.clientX, e.clientY);
        if (!pos) return;
        setPlacements(prev => ({ ...prev, [id]: pos }));
    };

    // Seçilen alanı ekranı dolduracak şekilde yakınlaştırır (seçimin merkezi görünümün merkezine gelir).
    const applyZoomSelect = () => {
        if (zoomSelectStart && zoomSelectCur) {
            const x1 = Math.min(zoomSelectStart.x, zoomSelectCur.x);
            const y1 = Math.min(zoomSelectStart.y, zoomSelectCur.y);
            const x2 = Math.max(zoomSelectStart.x, zoomSelectCur.x);
            const y2 = Math.max(zoomSelectStart.y, zoomSelectCur.y);
            const w = x2 - x1, h = y2 - y1;
            if (w >= 2 && h >= 2) {
                const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2;
                const newZoom = Math.min(20, Math.max(1, Math.min(100 / w, 100 / h)));
                const p = clampPan(50 / newZoom - cx, 50 / newZoom - cy, newZoom);
                setZoom(newZoom);
                setPanX(p.x);
                setPanY(p.y);
            }
        }
        setZoomSelectMode(false);
        setZoomSelectStart(null);
        setZoomSelectCur(null);
    };

    // Görünümün merkezini sabit tutarak kademeli uzaklaştırır.
    const handleZoomOut = () => {
        const centerX = 50 / zoom - panX;
        const centerY = 50 / zoom - panY;
        const nz = Math.max(1, zoom / 1.6);
        if (nz <= 1.001) {
            setZoom(1); setPanX(0); setPanY(0);
            return;
        }
        const p = clampPan(50 / nz - centerX, 50 / nz - centerY, nz);
        setZoom(nz); setPanX(p.x); setPanY(p.y);
    };

    const handleZoomReset = () => {
        setZoom(1); setPanX(0); setPanY(0);
        setZoomSelectMode(false); setZoomSelectStart(null); setZoomSelectCur(null);
    };

    // --- Canvas mouse drag (reposition) ---
    const handleDotMouseDown = (e: React.MouseEvent, daireID: number) => {
        e.preventDefault();
        e.stopPropagation();
        mouseMovedRef.current = false;
        if (rectMode) {
            // In rect mode: clicking dot selects/deselects it as target
            setSelectedDaireForRect(prev => prev === daireID ? null : daireID);
            setRectDrawStart(null);
            setRectDrawCur(null);
            return;
        }
        const cur0 = getCanvasPercent(e.clientX, e.clientY);
        if (!cur0) return;
        const cur = placements[daireID] ?? cur0;
        setDraggingOnCanvas(daireID);
        setDragOffset({ ox: cur0.x - cur.x, oy: cur0.y - cur.y });
    };

    // Bir alanın köşe/kenar tutamacına ya da gövdesine basılınca sürükleme başlatır.
    // Tuval'in kendi onMouseDown'ının (yeni alan çizmeye başlaması) tetiklenmemesi için
    // çağıran yerde e.stopPropagation() kullanılmalı.
    const startRectHandleDrag = (e: React.MouseEvent, daireID: number, kind: RectHandleKind | 'move') => {
        e.preventDefault();
        e.stopPropagation();
        const pos = getCanvasPercent(e.clientX, e.clientY);
        const p = placements[daireID];
        if (!pos || !p || p.rx1 === undefined || p.ry1 === undefined || p.rx2 === undefined || p.ry2 === undefined) return;
        setRectDrag({ daireID, kind, startPos: pos, startRect: { rx1: p.rx1, ry1: p.ry1, rx2: p.rx2, ry2: p.ry2 } });
    };

    const removeRect = (daireID: number) => {
        setPlacements(prev => {
            const n = { ...prev };
            if (n[daireID]) {
                const p = { ...n[daireID] };
                delete p.rx1; delete p.ry1; delete p.rx2; delete p.ry2;
                n[daireID] = p;
            }
            return n;
        });
    };

    const handleCanvasMouseDown = (e: React.MouseEvent) => {
        mouseMovedRef.current = false;
        if (zoomSelectMode) {
            const pos = getCanvasPercent(e.clientX, e.clientY);
            if (pos) { setZoomSelectStart(pos); setZoomSelectCur(pos); }
            return;
        }
        if (!readOnly && rectMode && selectedDaireForRect) {
            const pos = getCanvasPercent(e.clientX, e.clientY);
            if (pos) { setRectDrawStart(pos); setRectDrawCur(pos); }
            return;
        }
        if (!rectMode && zoom > 1) {
            panStartRef.current = { clientX: e.clientX, clientY: e.clientY, panX, panY };
            setIsPanning(true);
        }
    };

    const handleCanvasMouseMove = (e: React.MouseEvent) => {
        if (rectDrag) {
            const pos = getCanvasPercent(e.clientX, e.clientY);
            if (!pos) return;
            const { daireID, kind, startPos, startRect } = rectDrag;
            let { rx1, ry1, rx2, ry2 } = startRect;
            if (kind === 'move') {
                const dx = pos.x - startPos.x, dy = pos.y - startPos.y;
                const w = rx2 - rx1, h = ry2 - ry1;
                rx1 = Math.max(0, Math.min(100 - w, startRect.rx1 + dx));
                ry1 = Math.max(0, Math.min(100 - h, startRect.ry1 + dy));
                rx2 = rx1 + w; ry2 = ry1 + h;
            } else {
                const eff = RECT_HANDLE_EFFECTS[kind];
                if (eff.x === 'rx1') rx1 = Math.max(0, Math.min(pos.x, rx2 - RECT_MIN_SIZE));
                if (eff.x === 'rx2') rx2 = Math.min(100, Math.max(pos.x, rx1 + RECT_MIN_SIZE));
                if (eff.y === 'ry1') ry1 = Math.max(0, Math.min(pos.y, ry2 - RECT_MIN_SIZE));
                if (eff.y === 'ry2') ry2 = Math.min(100, Math.max(pos.y, ry1 + RECT_MIN_SIZE));
            }
            setPlacements(prev => ({ ...prev, [daireID]: { ...prev[daireID], rx1, ry1, rx2, ry2 } }));
            return;
        }
        if (zoomSelectMode) {
            if (zoomSelectStart) {
                const pos = getCanvasPercent(e.clientX, e.clientY);
                if (pos) setZoomSelectCur(pos);
            }
            return;
        }
        if (rectMode) {
            if (!readOnly && rectDrawStart) {
                const pos = getCanvasPercent(e.clientX, e.clientY);
                if (pos) setRectDrawCur(pos);
            }
            return;
        }
        if (isPanning && panStartRef.current) {
            mouseMovedRef.current = true;
            const el = canvasRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const dxPct = (((e.clientX - panStartRef.current.clientX) / rect.width) * 100) / zoom;
            const dyPct = (((e.clientY - panStartRef.current.clientY) / rect.height) * 100) / zoom;
            const p = clampPan(panStartRef.current.panX + dxPct, panStartRef.current.panY + dyPct, zoom);
            setPanX(p.x);
            setPanY(p.y);
            return;
        }
        if (readOnly) return;
        if (draggingOnCanvas !== null) mouseMovedRef.current = true;
        if (draggingOnCanvas === null || !dragOffset) return;
        const pos = getCanvasPercent(e.clientX, e.clientY);
        if (!pos) return;
        let nx = Math.max(0, Math.min(100, pos.x - dragOffset.ox));
        let ny = Math.max(0, Math.min(100, pos.y - dragOffset.oy));
        // Hizalama: diğer yerleştirilmiş noktalardan biri ekranda ~DOT_SNAP_PX içindeyse o noktanın
        // x/y'sine tam hizalanır; eşik zoom'a göre hesaplanır (yüksek zoom'da % olarak daha küçük eşik).
        const threshX = (DOT_SNAP_PX * 100) / Math.max(1, canvasSize.width * zoom);
        const threshY = (DOT_SNAP_PX * 100) / Math.max(1, canvasSize.height * zoom);
        let snapX: number | null = null, snapY: number | null = null;
        Object.entries(placements).forEach(([idStr, p]) => {
            if (Number(idStr) === draggingOnCanvas) return;
            if (snapX === null && Math.abs(p.x - nx) < threshX) snapX = p.x;
            if (snapY === null && Math.abs(p.y - ny) < threshY) snapY = p.y;
        });
        if (snapX !== null) nx = snapX;
        if (snapY !== null) ny = snapY;
        setAlignGuide({ x: snapX, y: snapY });
        setPlacements(prev => ({
            ...prev,
            [draggingOnCanvas]: { ...prev[draggingOnCanvas], x: nx, y: ny }
        }));
    };

    const handleCanvasMouseUp = () => {
        if (rectDrag) {
            setRectDrag(null);
            return;
        }
        if (zoomSelectMode) {
            applyZoomSelect();
            return;
        }
        if (isPanning) {
            setIsPanning(false);
            panStartRef.current = null;
            return;
        }
        if (rectMode && rectDrawStart && rectDrawCur && selectedDaireForRect) {
            const rx1 = Math.min(rectDrawStart.x, rectDrawCur.x);
            const ry1 = Math.min(rectDrawStart.y, rectDrawCur.y);
            const rx2 = Math.max(rectDrawStart.x, rectDrawCur.x);
            const ry2 = Math.max(rectDrawStart.y, rectDrawCur.y);
            if (rx2 - rx1 > 0.5 && ry2 - ry1 > 0.5) {
                setPlacements(prev => ({
                    ...prev,
                    [selectedDaireForRect]: { ...prev[selectedDaireForRect], rx1, ry1, rx2, ry2 }
                }));
            }
            setRectDrawStart(null);
            setRectDrawCur(null);
            return;
        }
        setDraggingOnCanvas(null);
        setDragOffset(null);
        setAlignGuide({ x: null, y: null });
    };

    const removePlacement = (daireID: number) => {
        setPlacements(prev => {
            const next = { ...prev };
            delete next[daireID];
            return next;
        });
    };

    const getDaire = (id: number) => daireler.find(d => d.daireID === id);

    const openDetay = async (daireID: number) => {
        const d = getDaire(daireID);
        if (!d || d.daireDurumu !== 'Satışta') return;
        setDetayDaire(d);
        setSunumModal(false);
        setSunumEklendi(new Set());
        setModalPos(null);
        setGaleriGorseller([]);
        setGaleriSlide(0);
        if (d.takipNo) {
            setGaleriYukleniyor(true);
            try {
                const dRes = await axiosInstance.get(`/api/SunumBelgeleri/takip/${d.takipNo}`);
                const dGorseller: SunumBelge[] = (dRes.data ?? []).filter((b: SunumBelge) => b.sunumBelgeTuru === 'Görsel');
                if (dGorseller.length > 0) {
                    setGaleriGorseller(dGorseller);
                } else if (d.proje?.takipNo) {
                    const pRes = await axiosInstance.get(`/api/SunumBelgeleri/takip/${d.proje.takipNo}`);
                    setGaleriGorseller((pRes.data ?? []).filter((b: SunumBelge) => b.sunumBelgeTuru === 'Görsel'));
                }
            } catch { /* sessiz */ }
            finally { setGaleriYukleniyor(false); }
        }
    };

    const openSunumaEkle = async () => {
        setSunumModal(true);
        setSunumYukleniyor(true);
        try {
            const res = await axiosInstance.get('/api/MusteriSunumlari');
            const aktif = (res.data as SunumListeItem[]).filter(
                s => s.durum === 'Taslak' || s.durum === 'Devam Ediyor'
            );
            setSunumlar(aktif);
            // Zaten eklenmiş olan sunumları işaretle
            if (detayDaire) {
                const ekliSet = new Set(
                    aktif.filter(s => s.daireler.some(d => d.daireID === detayDaire.daireID))
                        .map(s => s.sunumID)
                );
                setSunumEklendi(ekliSet);
            }
        } catch { toast.error('Sunumlar yüklenemedi.'); }
        finally { setSunumYukleniyor(false); }
    };

    const handleSunumaEkle = async (sunumID: number) => {
        if (!detayDaire) return;
        setSunumEkleniyor(sunumID);
        try {
            await axiosInstance.post(`/api/MusteriSunumlari/${sunumID}/daireler`, [detayDaire.daireID]);
            setSunumEklendi(prev => new Set(Array.from(prev).concat(sunumID)));
            toast.success('Daire sunuma eklendi.');
        } catch { toast.error('Ekleme başarısız.'); }
        finally { setSunumEkleniyor(null); }
    };

    // "Cephe" seçici — görünümün üzerine overlay olarak binen açılır kutu (hem düzenleme hem önizleme modunda kullanılır)
    const renderCepheOverlay = (currentCepheID: number | null, onSelect: (id: number) => void, allowManage: boolean) => (
        <div style={{position:'absolute',top:10,left:10,zIndex:40,display:'flex',flexDirection:'column',alignItems:'flex-start',gap:6}}>
            <div style={{display:'flex',alignItems:'center',gap:6,background:'rgba(255,255,255,0.95)',backdropFilter:'blur(4px)',borderRadius:9,padding:'5px 8px',boxShadow:'0 2px 10px rgba(15,23,42,.15)',border:'1px solid #e2e8f0'}}>
                <span style={{fontSize:10.5,fontWeight:800,color:'#94a3b8',letterSpacing:'.3px'}}>CEPHE</span>
                <select
                    value={currentCepheID ?? ''}
                    onChange={e => onSelect(Number(e.target.value))}
                    style={{height:26,border:'1px solid #e2e8f0',borderRadius:7,padding:'0 6px',fontSize:12,fontWeight:600,color:'#1e293b',background:'#fff',outline:'none',cursor:'pointer',maxWidth:150}}
                >
                    {cepheListSorted.length === 0 && <option value="">Cephe yok</option>}
                    {cepheListSorted.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}{!c.gorselYolu ? ' (görselsiz)' : ''}</option>)}
                </select>
                {allowManage && currentCepheID && (() => {
                    const c = cepheListSorted.find(x => x.cepheID === currentCepheID);
                    const idx = cepheListSorted.findIndex(x => x.cepheID === currentCepheID);
                    if (!c) return null;
                    return <>
                        <button className="pgv-icon-btn" style={{width:22,height:22,fontSize:9}} title="Yukarı Taşı" disabled={idx === 0}
                            onClick={() => handleMoveCephe(c, -1)}><i className="fas fa-chevron-up"/></button>
                        <button className="pgv-icon-btn" style={{width:22,height:22,fontSize:9}} title="Aşağı Taşı" disabled={idx === cepheListSorted.length - 1}
                            onClick={() => handleMoveCephe(c, 1)}><i className="fas fa-chevron-down"/></button>
                        <button className="pgv-icon-btn edit" style={{width:22,height:22,fontSize:9}} title={t('btn.duzenle')}
                            onClick={() => { setEditingCephe(c); setCepheFormAdi(c.cepheAdi); setShowCepheForm(true); }}><i className="fas fa-pen"/></button>
                        <button className="pgv-icon-btn" style={{width:22,height:22,fontSize:9}} title={t('btn.sil')}
                            onClick={() => handleDeleteCephe(c.cepheID)}><i className="fas fa-trash"/></button>
                    </>;
                })()}
                {allowManage && !showCepheForm && (
                    <button className="pgv-add-btn" style={{height:22,fontSize:10}} onClick={() => { setEditingCephe(null); setCepheFormAdi(''); setShowCepheForm(true); }}>+ Cephe</button>
                )}
            </div>
            {allowManage && showCepheForm && (
                <div style={{display:'flex',alignItems:'center',gap:6,background:'rgba(255,255,255,0.97)',backdropFilter:'blur(4px)',borderRadius:9,padding:'6px 8px',boxShadow:'0 2px 10px rgba(15,23,42,.15)',border:'1px solid #e2e8f0'}}>
                    <input className="pgv-input" style={{height:26,width:140,fontSize:11.5}} autoFocus
                        placeholder="ör: Kuzey Cephe" value={cepheFormAdi} onChange={e => setCepheFormAdi(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') editingCephe ? handleRenameCephe() : handleAddCephe(); if (e.key === 'Escape') { setShowCepheForm(false); setEditingCephe(null); } }} />
                    <button className="pgv-btn pgv-btn-primary" style={{height:26,fontSize:11}} disabled={cepheSaving || !cepheFormAdi.trim()}
                        onClick={() => editingCephe ? handleRenameCephe() : handleAddCephe()}>{cepheSaving ? '...' : 'Kaydet'}</button>
                    <button className="pgv-btn pgv-btn-ghost" style={{height:26,fontSize:11}} onClick={() => { setShowCepheForm(false); setEditingCephe(null); }}>{t('btn.iptal')}</button>
                </div>
            )}
        </div>
    );

    // Esc → önce tam ekrandan çık (modalı kapatma)
    useEffect(() => {
        if (!tamEkran) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setTamEkran(false); } };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [tamEkran]);

    const panelDaireler = daireler.filter(d =>
        d.aktifMi &&
        (!filterPanel || d.daireNo.toLowerCase().includes(filterPanel.toLowerCase())) &&
        (!filterPanelBlok || d.blokID === filterPanelBlok) &&
        (!filterPanelKat || d.katID === filterPanelKat)
    );

    // Nokta boyutu, tuvalin gerçek render boyutuna orantılı (doğal görsel çözünürlüğüne değil) —
    // farklı ekran genişliklerinde (dizüstü, geniş ekran, tam ekran) tutarlı görünür.
    const dotSizePx = Math.max(10, Math.min(16, canvasSize.width * 0.012));
    const dotRadiusPx = dotSizePx / 2;

    // Etiket (daire no) katmanı için: yerleştirilmiş, görünür durumdaki dairelerin ekran
    // piksel konumu (zoom/pan uygulanmış) — zoom/pan'dan bağımsız bir üst katmanda çizilir
    // ki metin zoom ile büyüyüp küçülmesin, noktayla aynı sabit görünür boyutu korusun.
    const labelMarkersInput: LabelMarkerInput[] = React.useMemo(() => {
        if (!canvasSize.width || !canvasSize.height) return [];
        const out: LabelMarkerInput[] = [];
        Object.entries(placements).forEach(([idStr, pos]) => {
            const daireID = Number(idStr);
            const d = daireler.find(x => x.daireID === daireID);
            if (!d || !visibleDurumlar.has(getDurumKey(d.daireDurumu))) return;
            const screenXPct = (pos.x + panX) * zoom;
            const screenYPct = (pos.y + panY) * zoom;
            out.push({
                daireID,
                ax: (screenXPct / 100) * canvasSize.width,
                ay: (screenYPct / 100) * canvasSize.height,
                text: d.daireNo,
            });
        });
        return out;
    }, [placements, zoom, panX, panY, canvasSize, visibleDurumlar, daireler]);

    const labelLayout = React.useMemo(
        () => computeLabelLayout(labelMarkersInput, dotRadiusPx),
        [labelMarkersInput, dotRadiusPx]
    );

    const labelRenderData = React.useMemo(() => labelLayout.map(l => {
        const marker = labelMarkersInput.find(m => m.daireID === l.daireID);
        const d = daireler.find(x => x.daireID === l.daireID);
        const meta = durumMeta[d?.daireDurumu ?? ''] ?? { bg: '#94a3b8', border: '#64748b', text: '#fff' };
        return {
            ...l,
            ax: marker?.ax ?? 0, ay: marker?.ay ?? 0,
            daireNo: d?.daireNo ?? '',
            satista: d?.daireDurumu === 'Satışta',
            bg: meta.bg,
        };
    }), [labelLayout, labelMarkersInput, daireler]);

    return (
        <div className={`pgv-overlay${tamEkran ? ' tam-ekran' : ''}`} onClick={() => { if (tamEkran) setTamEkran(false); else onClose(); }}>
            <div className={`pgv-modal${tamEkran ? ' tam-ekran' : ''}`} onClick={e => e.stopPropagation()}>
                <style>{`
                    .pgv-overlay{position:fixed;inset:0;background:rgba(15,23,42,.35);backdrop-filter:blur(3px);z-index:9999;display:flex;align-items:center;justify-content:center;padding:12px}
                    .pgv-modal{background:#fff;border-radius:14px;box-shadow:0 24px 80px rgba(15,23,42,.22);width:100%;max-width:calc(100vw - 24px);height:calc(100vh - 24px);display:flex;flex-direction:column;overflow:hidden;font-family:'Inter',sans-serif}
                    .pgv-head{padding:14px 20px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;flex-shrink:0}
                    .pgv-title{font-size:15px;font-weight:700;color:#0f172a;display:flex;align-items:center;gap:8px}
                    .pgv-close{width:32px;height:32px;border:none;background:none;cursor:pointer;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:18px}
                    .pgv-close:hover{background:#f1f5f9;color:#0f172a}
                    .pgv-body{display:flex;flex:1;overflow:hidden}
                    .pgv-sidebar{width:220px;flex-shrink:0;border-right:1px solid #e2e8f0;display:flex;flex-direction:column;overflow:hidden;background:#fafbfc}
                    .pgv-sidebar-head{padding:10px 14px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between}
                    .pgv-sidebar-title{font-size:12px;font-weight:700;color:#475569;letter-spacing:.4px}
                    .pgv-add-btn{height:26px;padding:0 10px;font-size:11px;font-weight:600;background:#cd6906;color:#fff;border:none;border-radius:7px;cursor:pointer}
                    .pgv-add-btn:hover{background:#b85d05}
                    .pgv-gorunum-list{flex:1;overflow-y:auto;padding:8px}
                    .pgv-gorunum-item{padding:9px 10px;border-radius:8px;cursor:pointer;display:flex;align-items:center;justify-content:space-between;margin-bottom:3px;transition:background .12s}
                    .pgv-gorunum-item:hover{background:#f1f5f9}
                    .pgv-gorunum-item.active{background:#fff8f0;border:1px solid #fde8cc}
                    .pgv-gorunum-name{font-size:12.5px;font-weight:600;color:#0f172a;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
                    .pgv-gorunum-desc{font-size:11px;color:#94a3b8;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
                    .pgv-gorunum-actions{display:flex;gap:3px;opacity:0;transition:opacity .15s}
                    .pgv-gorunum-item:hover .pgv-gorunum-actions{opacity:1}
                    .pgv-icon-btn{width:22px;height:22px;border:none;background:none;cursor:pointer;border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:11px;color:#94a3b8}
                    .pgv-icon-btn:hover{background:#fef2f2;color:#dc2626}
                    .pgv-icon-btn.edit:hover{background:#f0f9ff;color:#0369a1}
                    .pgv-center{flex:1;display:flex;flex-direction:column;overflow:hidden}
                    .pgv-toolbar{padding:8px 14px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;gap:8px;flex-shrink:0;background:#fff;flex-wrap:wrap}
                    .pgv-efekt-bar{padding:6px 14px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;gap:5px;flex-shrink:0;background:#fafbfc;overflow-x:auto;scrollbar-width:none}
                    .pgv-efekt-bar::-webkit-scrollbar{display:none}
                    .pgv-efekt-chip{height:26px;padding:0 10px;border-radius:20px;font-size:11px;font-weight:600;border:1.5px solid #e2e8f0;background:#fff;cursor:pointer;white-space:nowrap;display:inline-flex;align-items:center;gap:4px;transition:all .12s;color:#475569}
                    .pgv-efekt-chip:hover{border-color:#cd6906;color:#cd6906;background:#fff8f0}
                    .pgv-efekt-chip.active{border-color:#cd6906;background:#cd6906;color:#fff}
                    .pgv-canvas-wrap{flex:1;overflow:hidden;display:flex;flex-direction:column;padding:14px;gap:10px;background:#f4f6fb;min-height:0}
                    .pgv-canvas-frame{flex:1;display:flex;align-items:center;justify-content:center;overflow:hidden;min-height:0;width:100%}
                    .pgv-canvas-ar{position:relative;height:100%;width:auto;aspect-ratio:4/3;max-width:100%;overflow:hidden;border-radius:10px;border:2px dashed #e2e8f0;background:#f8fafc;cursor:crosshair}
                    .pgv-canvas-ar.has-bg{border-style:solid;border-color:#e2e8f0}
                    .pgv-canvas-inner{position:absolute;inset:0}
                    .pgv-zoom-layer{position:absolute;inset:0;transform-origin:0 0;transition:transform .25s ease}
                    .pgv-canvas-inner img{width:100%;height:100%;object-fit:fill;border-radius:8px;display:block}
                    .pgv-canvas-empty{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#94a3b8;pointer-events:none}
                    .pgv-daire-dot{position:absolute;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:700;color:#fff;cursor:grab;border:2px solid transparent;transition:box-shadow .15s;user-select:none}
                    .pgv-daire-dot.satista{cursor:pointer;}
                    .pgv-daire-dot.satista:hover{box-shadow:0 0 0 5px rgba(34,197,94,.3)!important;}
                    .pgv-daire-dot:hover{box-shadow:0 0 0 4px rgba(0,0,0,.15)}
                    .pgv-daire-dot.dragging-canvas{cursor:grabbing}
                    .pgv-daire-dot.rect-mode{cursor:pointer}
                    .pgv-daire-dot.rect-selected{box-shadow:0 0 0 4px #cd6906,0 0 0 7px rgba(205,105,6,.25)}
                    .pgv-daire-rect{position:absolute;border-radius:4px;transition:background .15s;pointer-events:none;box-sizing:border-box;border:2px dashed}
                    .pgv-rect-info-bar{padding:6px 14px;background:#fff8f0;border-bottom:1px solid #fde8cc;display:flex;align-items:center;gap:8px;font-size:11.5px;color:#92400e;flex-shrink:0}
                    .pgv-tooltip{position:fixed;background:#1e293b;color:#fff;border-radius:10px;padding:10px 12px;font-size:12px;z-index:99999;pointer-events:none;min-width:160px;box-shadow:0 8px 24px rgba(0,0,0,.25)}
                    .pgv-tooltip-title{font-weight:700;font-size:13px;margin-bottom:6px}
                    .pgv-tooltip-row{display:flex;justify-content:space-between;gap:16px;margin-bottom:2px;opacity:.9}
                    .pgv-tooltip-badge{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:20px;font-size:10.5px;font-weight:600;margin-top:4px}
                    .pgv-panel{width:220px;flex-shrink:0;border-left:1px solid #e2e8f0;display:flex;flex-direction:column;overflow:hidden;background:#fafbfc}
                    .pgv-panel-head{padding:10px 12px;border-bottom:1px solid #e2e8f0;font-size:11.5px;font-weight:700;color:#475569}
                    .pgv-panel-filter{padding:8px 10px;border-bottom:1px solid #e2e8f0;display:flex;flex-direction:column;gap:5px}
                    .pgv-panel-input{width:100%;height:28px;border:1px solid #e2e8f0;border-radius:7px;padding:0 8px;font-size:11.5px;outline:none;background:#fff;box-sizing:border-box}
                    .pgv-panel-select{width:100%;height:28px;border:1px solid #e2e8f0;border-radius:7px;padding:0 6px;font-size:11.5px;outline:none;background:#fff;box-sizing:border-box;cursor:pointer}
                    .pgv-panel-select:focus{border-color:#cd6906}
                    .pgv-panel-list{flex:1;overflow-y:auto;padding:6px}
                    .pgv-panel-daire{padding:7px 9px;border-radius:7px;margin-bottom:3px;cursor:grab;display:flex;align-items:center;gap:7px;border:1px solid transparent;transition:background .12s;}
                    .pgv-panel-daire:hover{background:#fff;border-color:#e2e8f0}
                    .pgv-panel-daire.on-canvas{opacity:.55}
                    .pgv-panel-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}
                    .pgv-panel-daire-no{font-size:12px;font-weight:600;color:#0f172a;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
                    .pgv-panel-placed{font-size:9px;color:#94a3b8;white-space:nowrap}
                    .pgv-form-card{background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:14px;margin:10px}
                    .pgv-form-title{font-size:13px;font-weight:700;color:#0f172a;margin-bottom:10px}
                    .pgv-label{display:block;font-size:11.5px;font-weight:600;color:#64748b;margin-bottom:4px}
                    .pgv-input{width:100%;height:34px;border:1.5px solid #e2e8f0;border-radius:8px;padding:0 10px;font-size:13px;outline:none;font-family:inherit}
                    .pgv-input:focus{border-color:#cd6906}
                    .pgv-textarea{width:100%;border:1.5px solid #e2e8f0;border-radius:8px;padding:8px 10px;font-size:12.5px;outline:none;font-family:inherit;resize:vertical}
                    .pgv-textarea:focus{border-color:#cd6906}
                    .pgv-btn{height:34px;padding:0 16px;border-radius:8px;font-size:12.5px;font-weight:600;border:none;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
                    .pgv-btn-primary{background:#cd6906;color:#fff}
                    .pgv-btn-primary:hover{background:#b85d05}
                    .pgv-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;cursor:not-allowed}
                    .pgv-btn-ghost{background:#fff;border:1.5px solid #e2e8f0;color:#475569}
                    .pgv-btn-ghost:hover{border-color:#cd6906;color:#cd6906}
                    .pgv-empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;flex:1;color:#94a3b8;gap:10px;padding:40px}
                    .pgv-save-bar{padding:8px 14px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center;background:#fff;flex-shrink:0}
                    .pgv-legend{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
                    .pgv-legend-item{display:flex;align-items:center;gap:5px;font-size:11px;color:#475569}
                    .pgv-legend-dot{width:10px;height:10px;border-radius:50%}
                    .pgv-legend-checkbox{width:12px;height:12px;margin:0;cursor:pointer;accent-color:#cd6906}

                    /* ── Tam ekran ──────────────────────────────────────────
                       Kenar panelleri ve yardımcı çubuklar gizlenir; geriye
                       yalnızca 4:3 tuval kalır ve arka plan görseli ekranın
                       izin verdiği en büyük 4:3 alana yayılır. */
                    .pgv-overlay.tam-ekran{padding:0;background:#0b1120;backdrop-filter:none}
                    .pgv-modal.tam-ekran{width:100vw;max-width:100vw;height:100vh;border-radius:0;background:#0b1120}
                    .pgv-modal.tam-ekran .pgv-head{background:#0b1120;border-bottom-color:#1e293b}
                    .pgv-modal.tam-ekran .pgv-title,
                    .pgv-modal.tam-ekran .pgv-head span{color:#e2e8f0}
                    .pgv-modal.tam-ekran .pgv-sidebar,
                    .pgv-modal.tam-ekran .pgv-panel,
                    .pgv-modal.tam-ekran .pgv-save-bar{display:none}
                    .pgv-modal.tam-ekran .pgv-canvas-wrap{background:#0b1120;padding:0}
                    .pgv-modal.tam-ekran .pgv-canvas-ar{border:none;border-radius:0}
                    .pgv-modal.tam-ekran .pgv-canvas-inner img{border-radius:0}
                `}</style>

                {/* HEADER */}
                <div className="pgv-head">
                    <div className="pgv-title" style={{flexShrink:0}}>
                        <i className="fas fa-layer-group" style={{color:'#cd6906'}}></i>
                        Proje Görünümleri
                    </div>
                    {selectedGorunum && <>
                        <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0,paddingLeft:14,marginLeft:14,borderLeft:'1px solid #e2e8f0'}}>
                            <span style={{fontWeight:700,fontSize:13,color:'#0f172a',whiteSpace:'nowrap'}}>{selectedGorunum.gorunumAdi}</span>
                            {is3D && <span style={{fontSize:9,fontWeight:800,color:'#cd6906',background:'#fff7ed',border:'1px solid #fde8cc',borderRadius:5,padding:'1px 5px'}}>3D</span>}
                        </div>
                        {canvasGorsel && !previewMode && (
                            <div className="pgv-efekt-bar" style={{border:'none',padding:'0 0 0 14px',flex:1,minWidth:0}}>
                                <span style={{fontSize:11,fontWeight:700,color:'#94a3b8',marginRight:2,flexShrink:0}}>EFEKT:</span>
                                {cssEfektler.map(e => (
                                    <button
                                        key={e.key}
                                        className={`pgv-efekt-chip${activeEfekt === e.key ? ' active' : ''}`}
                                        onClick={() => setActiveEfekt(e.key)}
                                    >{e.icon} {e.label}</button>
                                ))}
                            </div>
                        )}
                    </>}
                    <div style={{flex:1}} />
                    <button className="pgv-close" style={{flexShrink:0}}
                        title={tamEkran ? 'Tam ekrandan çık (Esc)' : 'Tam ekran'}
                        onClick={() => setTamEkran(v => !v)}>
                        <i className={tamEkran ? 'fas fa-compress' : 'fas fa-expand'} style={{fontSize:14}} />
                    </button>
                    <button className="pgv-close" onClick={onClose} style={{flexShrink:0}}>✕</button>
                </div>

                <div className="pgv-body">
                    {/* LEFT SIDEBAR — view list */}
                    <div className="pgv-sidebar">
                        <div className="pgv-sidebar-head">
                            <span className="pgv-sidebar-title">GÖRÜNÜMLER</span>
                            {!readOnly && <button className="pgv-add-btn" onClick={() => {
                                setEditingGorunum(null); setFormAdi(''); setFormAciklama(''); setFormGorunumTipi('2D');
                                setShowForm(true);
                            }}>+ Ekle</button>}
                        </div>
                        <div className="pgv-gorunum-list">
                            {!readOnly && showForm && (
                                <div className="pgv-form-card">
                                    <div className="pgv-form-title">{editingGorunum ? 'Düzenle' : 'Yeni Görünüm'}</div>
                                    <div style={{marginBottom:8}}>
                                        <label className="pgv-label">Ad *</label>
                                        <input className="pgv-input" value={formAdi} onChange={e => setFormAdi(e.target.value)} placeholder={t('label.ara')} maxLength={100} />
                                    </div>
                                    <div style={{marginBottom:10}}>
                                        <label className="pgv-label">{t('label.aciklama')}</label>
                                        <textarea className="pgv-textarea" rows={2} value={formAciklama} onChange={e => setFormAciklama(e.target.value)} placeholder={t('label.aciklama')} maxLength={500} />
                                    </div>
                                    {!editingGorunum && (
                                        <div style={{marginBottom:10}}>
                                            <label className="pgv-label">Görünüm Türü</label>
                                            <div style={{display:'flex',gap:6}}>
                                                <button type="button" onClick={() => setFormGorunumTipi('2D')}
                                                    style={{flex:1,height:32,borderRadius:8,fontSize:11.5,fontWeight:700,cursor:'pointer',
                                                        border: formGorunumTipi === '2D' ? '1.5px solid #cd6906' : '1.5px solid #e2e8f0',
                                                        background: formGorunumTipi === '2D' ? '#fff8f0' : '#fff',
                                                        color: formGorunumTipi === '2D' ? '#cd6906' : '#64748b'}}>
                                                    <i className="fas fa-image" style={{marginRight:5}}></i>2D — Tek Görsel
                                                </button>
                                                <button type="button" onClick={() => setFormGorunumTipi('3D')}
                                                    style={{flex:1,height:32,borderRadius:8,fontSize:11.5,fontWeight:700,cursor:'pointer',
                                                        border: formGorunumTipi === '3D' ? '1.5px solid #cd6906' : '1.5px solid #e2e8f0',
                                                        background: formGorunumTipi === '3D' ? '#fff8f0' : '#fff',
                                                        color: formGorunumTipi === '3D' ? '#cd6906' : '#64748b'}}>
                                                    <i className="fas fa-cube" style={{marginRight:5}}></i>3D — Çoklu Cephe
                                                </button>
                                            </div>
                                            <div style={{fontSize:10.5,color:'#94a3b8',marginTop:5}}>
                                                {formGorunumTipi === '3D'
                                                    ? 'Binanın farklı cephelerden çekilmiş birden fazla fotoğrafını yükleyip aralarında geçiş yapılabilecek bir görünüm oluşturursunuz.'
                                                    : 'Tek bir arka plan fotoğrafı üzerine daireleri işaretlersiniz.'}
                                            </div>
                                        </div>
                                    )}
                                    <div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
                                        <button className="pgv-btn pgv-btn-ghost" style={{height:28,fontSize:11}} onClick={() => setShowForm(false)}>{t('btn.iptal')}</button>
                                        <button className="pgv-btn pgv-btn-primary" style={{height:28,fontSize:11}} onClick={handleSaveForm} disabled={saving}>
                                            {saving ? '...' : 'Kaydet'}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {gorunumler.length === 0 && !showForm && (
                                <div style={{padding:'24px 10px',textAlign:'center',color:'#94a3b8',fontSize:12}}>Henüz görünüm eklenmemiş</div>
                            )}
                            {gorunumler.map(g => (
                                <div key={g.gorunumID}
                                    className={`pgv-gorunum-item${selectedID === g.gorunumID ? ' active' : ''}`}
                                    onClick={() => selectGorunum(g.gorunumID)}>
                                    <div style={{flex:1,minWidth:0}}>
                                        <div className="pgv-gorunum-name">
                                            {g.gorunumAdi}
                                            {g.gorunumTipi === '3D' && (
                                                <span style={{marginLeft:6,fontSize:9,fontWeight:800,color:'#cd6906',background:'#fff7ed',border:'1px solid #fde8cc',borderRadius:5,padding:'1px 5px'}}>3D</span>
                                            )}
                                        </div>
                                        {g.aciklama && <div className="pgv-gorunum-desc">{g.aciklama}</div>}
                                        <div style={{fontSize:10,color:'#94a3b8',marginTop:2}}>
                                            {g.gorunumTipi === '3D' ? `${g.cepheler.length} cephe` : `${g.daireler.length} daire`}
                                        </div>
                                    </div>
                                    {!readOnly && <div className="pgv-gorunum-actions">
                                        <button className="pgv-icon-btn edit" title="Görseller / Daireler / Bilgileri Düzenle" onClick={e => {
                                            e.stopPropagation();
                                            setSelectedID(g.gorunumID);
                                            setPreviewMode(false);
                                            setEditingGorunum(g); setFormAdi(g.gorunumAdi); setFormAciklama(g.aciklama ?? '');
                                            setFormGorunumTipi(g.gorunumTipi === '3D' ? '3D' : '2D');
                                            setShowForm(true);
                                        }}><i className="fas fa-pen"></i></button>
                                        <button className="pgv-icon-btn" title={t('btn.sil')} onClick={e => { e.stopPropagation(); handleDelete(g.gorunumID); }}>
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>}
                                </div>
                            ))}
                        </div>
                        {/* Durum Legend — 2x2 */}
                        <div style={{padding:'10px 14px',borderTop:'1px solid #e2e8f0',flexShrink:0,background:'#fafbfc'}}>
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 10px'}}>
                                {Object.entries(durumMeta).map(([durum, meta]) => (
                                    <label key={durum} className="pgv-legend-item" style={{cursor:'pointer'}}>
                                        <input type="checkbox" className="pgv-legend-checkbox" checked={visibleDurumlar.has(durum)} onChange={() => toggleDurumVisible(durum)} />
                                        <div className="pgv-legend-dot" style={{background:meta.bg}}/>
                                        {durum}
                                    </label>
                                ))}
                                <label className="pgv-legend-item" style={{cursor:'pointer'}}>
                                    <input type="checkbox" className="pgv-legend-checkbox" checked={visibleDurumlar.has('Diğer')} onChange={() => toggleDurumVisible('Diğer')} />
                                    <div className="pgv-legend-dot" style={{background:'#94a3b8'}}/>
                                    Diğer
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* CENTER — canvas */}
                    <div className="pgv-center">
                        {!selectedGorunum ? (
                            <div className="pgv-empty-state">
                                <i className="fas fa-layer-group" style={{fontSize:36,color:'#e2e8f0'}}></i>
                                <span style={{fontSize:13}}>{readOnly ? 'Sol panelden bir görünüm seçin' : 'Sol panelden bir görünüm seçin veya yeni ekleyin'}</span>
                            </div>
                        ) : (
                            <>
                                <div className="pgv-toolbar">
                                    {!previewMode && (!is3D || activeCepheID) && canvasGorsel && (
                                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                                            <button
                                                className={`pgv-btn${zoomSelectMode ? ' pgv-btn-primary' : ' pgv-btn-ghost'}`}
                                                style={{height:30,fontSize:11}}
                                                onClick={() => {
                                                    setZoomSelectMode(v => !v);
                                                    setRectMode(false); setSelectedDaireForRect(null); setRectDrawStart(null); setRectDrawCur(null);
                                                    setZoomSelectStart(null); setZoomSelectCur(null);
                                                }}
                                                title="Fare ile bir alan seçerek o bölgeye yakınlaşın"
                                            >
                                                <i className="fas fa-search-plus"></i>{zoomSelectMode ? 'Alan Seçin...' : 'Yakınlaştır'}
                                            </button>
                                            {zoom > 1 && <>
                                                <button className="pgv-btn pgv-btn-ghost" style={{height:30,fontSize:11}} onClick={handleZoomOut} title="Uzaklaştır">
                                                    <i className="fas fa-search-minus"></i>
                                                </button>
                                                <button className="pgv-btn pgv-btn-ghost" style={{height:30,fontSize:11}} onClick={handleZoomReset} title="Tüm fotoğrafı göster">
                                                    <i className="fas fa-compress"></i> Tüm Fotoğraf <span style={{opacity:.6,fontWeight:400}}>({Math.round(zoom * 100)}%)</span>
                                                </button>
                                            </>}
                                        </div>
                                    )}
                                    <div style={{flex:1}}/>
                                    {!readOnly && !previewMode && (!is3D || activeCepheID) && <><input type="file" ref={fileInputRef} style={{display:'none'}} accept="image/*" onChange={handleGorselUpload} />
                                    <button className="pgv-btn pgv-btn-ghost" style={{height:30,fontSize:11}} onClick={() => fileInputRef.current?.click()} disabled={gorselUploading}>
                                        <i className="fas fa-image"></i>{gorselUploading ? t('msg.yukleniyor') : (is3D ? 'Cephe Görseli' : 'Arka Plan')}
                                    </button>
                                    <button
                                        className={`pgv-btn${rectMode ? ' pgv-btn-primary' : ' pgv-btn-ghost'}`}
                                        style={{height:30,fontSize:11}}
                                        onClick={() => {
                                            setRectMode(v => !v); setSelectedDaireForRect(null); setRectDrawStart(null); setRectDrawCur(null);
                                            setZoomSelectMode(false); setZoomSelectStart(null); setZoomSelectCur(null);
                                        }}
                                        title={t('label.tumunuSec')}
                                    >
                                        <i className="fas fa-vector-square"></i>{rectMode ? 'Alan Modu Aktif' : 'Alan Çiz'}
                                    </button></>
                                    }
                                    {!readOnly && !is3D && !previewMode && selectedGorunum.arkaplanGorsel && (
                                        <button className="pgv-btn pgv-btn-ghost" style={{height:30,fontSize:11,color:'#dc2626'}} onClick={async () => {
                                            try {
                                                await axiosInstance.post(`/api/ProjeGorunumleri/update/${selectedGorunum.gorunumID}`, {
                                                    gorunumAdi: selectedGorunum.gorunumAdi,
                                                    aciklama: selectedGorunum.aciklama,
                                                    siraNo: selectedGorunum.siraNo,
                                                    arkaplanGorsel: null
                                                });
                                                await fetchGorunumler();
                                            } catch {}
                                        }}>
                                            <i className="fas fa-times"></i> Arka Planı Kaldır
                                        </button>
                                    )}
                                </div>
                                {!previewMode && <>
                                {!readOnly && rectMode && (
                                    <div className="pgv-rect-info-bar">
                                        <i className="fas fa-vector-square" style={{color:'#cd6906'}}></i>
                                        <span><b>Alan Çizim Modu:</b> {selectedDaireForRect ? (
                                            placements[selectedDaireForRect]?.rx1 !== undefined
                                                ? <>Köşe/kenar tutamaçlarından yeniden boyutlandırın, gövdesinden sürükleyip taşıyın &mdash; Seçili: <b>{getDaire(selectedDaireForRect)?.daireNo}</b></>
                                                : <>Tuvalde sürükleyerek dikdörtgen çizin &mdash; Seçili: <b>{getDaire(selectedDaireForRect)?.daireNo}</b></>
                                        ) : 'Bir daire ikonuna tıklayarak seçin, sonra tuvalde çizin'}</span>
                                        {selectedDaireForRect && placements[selectedDaireForRect]?.rx1 !== undefined && (
                                            <button className="pgv-btn pgv-btn-ghost" style={{height:22,fontSize:10,marginLeft:8,color:'#dc2626',borderColor:'#fecaca'}}
                                                onClick={() => removeRect(selectedDaireForRect)}
                                            >Alanı Sil</button>
                                        )}
                                        <div style={{flex:1}}/>
                                        <span style={{fontSize:10,color:'#b45309'}}>ESC ile çıkış</span>
                                    </div>
                                )}
                                {zoomSelectMode && (
                                    <div className="pgv-rect-info-bar" style={{background:'#eff6ff',borderBottomColor:'#bfdbfe',color:'#1e40af'}}>
                                        <i className="fas fa-search-plus" style={{color:'#2563eb'}}></i>
                                        <span><b>Yakınlaştırma Modu:</b> Tuvalde bir alan sürükleyerek seçin, bıraktığınızda o bölgeye yakınlaşılır</span>
                                        <div style={{flex:1}}/>
                                        <span style={{fontSize:10,color:'#2563eb'}}>ESC ile çıkış</span>
                                    </div>
                                )}
                                <div className="pgv-canvas-wrap">
                                    <div className="pgv-canvas-frame">
                                    <div className="pgv-canvas-ar"
                                        ref={canvasRef}
                                        onDragOver={!readOnly ? e => e.preventDefault() : undefined}
                                        onDrop={!readOnly ? handleCanvasDrop : undefined}
                                        onMouseDown={handleCanvasMouseDown}
                                        onMouseMove={handleCanvasMouseMove}
                                        onMouseUp={handleCanvasMouseUp}
                                        onMouseLeave={handleCanvasMouseUp}
                                        style={{userSelect:'none', cursor: zoomSelectMode ? 'crosshair' : (rectMode && selectedDaireForRect) ? 'crosshair' : rectMode ? 'default' : zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : undefined}}
                                    >
                                        <div className="pgv-canvas-inner">
                                            {is3D && renderCepheOverlay(activeCepheID, id => setActiveCepheID(id), !readOnly)}
                                            <div className="pgv-zoom-layer" style={{ transform: `scale(${zoom}) translate(${panX}%, ${panY}%)`, transition: isPanning ? 'none' : undefined }}>
                                            {canvasGorsel ? (
                                                <img
                                                    src={`${API_BASE}/api/ProjeGorunumleri/gorsel/${canvasGorsel}`}
                                                    alt="arka plan"
                                                    draggable={false}
                                                    style={{filter: cssEfektler.find(e => e.key === activeEfekt)?.filter ?? 'none'}}
                                                />
                                            ) : (
                                                <div className="pgv-canvas-empty">
                                                    <i className="fas fa-image" style={{fontSize:32,color:'#d1d5db'}}></i>
                                                    <span style={{fontSize:12,color:'#94a3b8'}}>
                                                        {is3D
                                                            ? (activeCepheID ? '"Cephe Görseli" butonuna tıklayarak bu cepheye fotoğraf yükleyin' : 'Önce yukarıdan bir cephe seçin veya "+ Cephe" ile ekleyin')
                                                            : 'Arka plan eklemek için "Arka Plan" butonuna tıklayın'}
                                                    </span>
                                                    <span style={{fontSize:11,color:'#cbd5e1',marginTop:4}}>Daireleri sağ panelden sürükleyerek bu alana bırakın</span>
                                                </div>
                                            )}
                                            {/* Saved rects */}
                                            {Object.entries(placements).map(([idStr, pos]) => {
                                                if (pos.rx1 === undefined || pos.ry1 === undefined || pos.rx2 === undefined || pos.ry2 === undefined) return null;
                                                const daireID = Number(idStr);
                                                const d = getDaire(daireID);
                                                if (!d) return null;
                                                if (!visibleDurumlar.has(getDurumKey(d.daireDurumu))) return null;
                                                const meta = durumMeta[d.daireDurumu] ?? { bg: '#94a3b8', border: '#64748b', text: '#fff' };
                                                const isHovered = hoveredDaireID === daireID;
                                                const isSelected = selectedDaireForRect === daireID;
                                                // Bu alan, rect modunda seçiliyken tutamaçlarla yeniden boyutlandırılabilir/taşınabilir.
                                                const editable = !readOnly && rectMode && isSelected;
                                                return (
                                                    <React.Fragment key={`rect-${daireID}`}>
                                                        <div className="pgv-daire-rect" style={{
                                                            left: `${pos.rx1}%`, top: `${pos.ry1}%`,
                                                            width: `${pos.rx2 - pos.rx1}%`, height: `${pos.ry2 - pos.ry1}%`,
                                                            borderColor: isSelected ? '#cd6906' : meta.bg,
                                                            borderStyle: readOnly ? 'solid' : 'dashed',
                                                            borderWidth: readOnly ? 1.5 : 2,
                                                            // Görüntüleme (readOnly) modunda daire şeridini durum rengiyle yarı saydam doldurur;
                                                            // düzenleme modunda mevcut kesikli/şeffaf çizim davranışı korunur.
                                                            background: readOnly
                                                                ? hexToRgba(meta.bg, isHovered ? 0.45 : 0.28)
                                                                : (isHovered ? 'rgba(0,0,0,0.3)' : isSelected ? 'rgba(205,105,6,0.08)' : 'transparent'),
                                                            zIndex: isHovered ? 15 : 8,
                                                            pointerEvents: editable ? 'auto' : 'none',
                                                            cursor: editable ? 'move' : undefined,
                                                        }}
                                                        onMouseDown={editable ? e => startRectHandleDrag(e, daireID, 'move') : undefined}
                                                        />
                                                        {editable && (<>
                                                            {(Object.keys(RECT_HANDLE_EFFECTS) as RectHandleKind[]).map(kind => {
                                                                const hx = kind.includes('w') ? pos.rx1! : kind.includes('e') ? pos.rx2! : (pos.rx1! + pos.rx2!) / 2;
                                                                const hy = kind.includes('n') ? pos.ry1! : kind.includes('s') ? pos.ry2! : (pos.ry1! + pos.ry2!) / 2;
                                                                return (
                                                                    <div key={kind}
                                                                        onMouseDown={e => startRectHandleDrag(e, daireID, kind)}
                                                                        style={{
                                                                            position: 'absolute', left: `${hx}%`, top: `${hy}%`,
                                                                            width: 9, height: 9, marginLeft: -4.5, marginTop: -4.5,
                                                                            background: '#fff', border: '2px solid #cd6906', borderRadius: 2,
                                                                            zIndex: 60, cursor: RECT_HANDLE_CURSOR[kind],
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                            <button
                                                                onMouseDown={e => e.stopPropagation()}
                                                                onClick={() => removeRect(daireID)}
                                                                title="Alanı sil"
                                                                style={{
                                                                    position: 'absolute', left: `${pos.rx2}%`, top: `${pos.ry1}%`,
                                                                    marginLeft: 4, marginTop: -22,
                                                                    width: 18, height: 18, borderRadius: '50%', background: '#dc2626',
                                                                    color: '#fff', border: 'none', fontSize: 10, lineHeight: 1, cursor: 'pointer',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 61,
                                                                    boxShadow: '0 1px 4px rgba(0,0,0,.3)',
                                                                }}
                                                            >✕</button>
                                                        </>)}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {/* Hizalama çizgileri — bir nokta sürüklenirken başka bir noktayla aynı hizaya gelince görünür */}
                                            {draggingOnCanvas !== null && alignGuide.x !== null && (
                                                <div style={{ position: 'absolute', left: `${alignGuide.x}%`, top: 0, bottom: 0, width: 1, background: '#2563eb', opacity: 0.85, zIndex: 55, pointerEvents: 'none' }} />
                                            )}
                                            {draggingOnCanvas !== null && alignGuide.y !== null && (
                                                <div style={{ position: 'absolute', top: `${alignGuide.y}%`, left: 0, right: 0, height: 1, background: '#2563eb', opacity: 0.85, zIndex: 55, pointerEvents: 'none' }} />
                                            )}
                                            {/* Live preview rect while drawing */}
                                            {rectMode && rectDrawStart && rectDrawCur && (() => {
                                                const selD = selectedDaireForRect ? getDaire(selectedDaireForRect) : null;
                                                const meta = selD ? (durumMeta[selD.daireDurumu] ?? { bg: '#94a3b8' }) : { bg: '#cd6906' };
                                                return (
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: `${Math.min(rectDrawStart.x, rectDrawCur.x)}%`,
                                                        top: `${Math.min(rectDrawStart.y, rectDrawCur.y)}%`,
                                                        width: `${Math.abs(rectDrawCur.x - rectDrawStart.x)}%`,
                                                        height: `${Math.abs(rectDrawCur.y - rectDrawStart.y)}%`,
                                                        border: `2px dashed ${meta.bg}`,
                                                        background: 'rgba(205,105,6,0.07)',
                                                        borderRadius: 4, zIndex: 50, pointerEvents: 'none', boxSizing: 'border-box',
                                                    }} />
                                                );
                                            })()}
                                            {/* Yakınlaştırma alanı seçim önizlemesi */}
                                            {zoomSelectMode && zoomSelectStart && zoomSelectCur && (
                                                <div style={{
                                                    position: 'absolute',
                                                    left: `${Math.min(zoomSelectStart.x, zoomSelectCur.x)}%`,
                                                    top: `${Math.min(zoomSelectStart.y, zoomSelectCur.y)}%`,
                                                    width: `${Math.abs(zoomSelectCur.x - zoomSelectStart.x)}%`,
                                                    height: `${Math.abs(zoomSelectCur.y - zoomSelectStart.y)}%`,
                                                    border: '2px dashed #2563eb',
                                                    background: 'rgba(37,99,235,0.12)',
                                                    borderRadius: 4, zIndex: 60, pointerEvents: 'none', boxSizing: 'border-box',
                                                }} />
                                            )}
                                            {/* Placed daires */}
                                            {Object.entries(placements).map(([idStr, pos]) => {
                                                const daireID = Number(idStr);
                                                const d = getDaire(daireID);
                                                if (!d) return null;
                                                if (!visibleDurumlar.has(getDurumKey(d.daireDurumu))) return null;
                                                const meta = durumMeta[d.daireDurumu] ?? { bg: '#94a3b8', border: '#64748b', text: '#fff' };
                                                const isBeingDragged = draggingOnCanvas === daireID;
                                                const isHoveredDot = hoveredDaireID === daireID;
                                                const isSelectedRect = selectedDaireForRect === daireID;
                                                // Yakınlaştırma seviyesinden bağımsız olarak ikon boyutu hep aynı kalsın diye ters ölçek uygulanır.
                                                const hoverScale = isBeingDragged ? 1.1 : isSelectedRect ? 1.15 : isHoveredDot ? 1.25 : 1;
                                                return (
                                                    <div
                                                        key={daireID}
                                                        className={`pgv-daire-dot${isBeingDragged ? ' dragging-canvas' : ''}${rectMode ? ' rect-mode' : ''}${isSelectedRect ? ' rect-selected' : ''}${d.daireDurumu === 'Satışta' ? ' satista' : ''}`}
                                                        style={{
                                                            left: `${pos.x}%`,
                                                            top: `${pos.y}%`,
                                                            width: dotSizePx,
                                                            height: dotSizePx,
                                                            background: meta.bg,
                                                            borderColor: meta.border,
                                                            transform: `translate(-50%,-50%) scale(${hoverScale / zoom})`,
                                                            zIndex: isBeingDragged ? 30 : isSelectedRect ? 25 : isHoveredDot ? 20 : 10,
                                                        }}
                                                        onMouseDown={!readOnly ? e => handleDotMouseDown(e, daireID) : undefined}
                                                        onClick={() => {
                                                            if (mouseMovedRef.current || rectMode) return;
                                                            const d = getDaire(daireID);
                                                            if (d?.daireDurumu === 'Satışta') openDetay(daireID);
                                                        }}
                                                        onMouseEnter={e => {
                                                            if (draggingOnCanvas !== null) return;
                                                            setHoveredDaireID(daireID);
                                                            setTooltipPos({ x: e.clientX + 14, y: e.clientY - 10 });
                                                        }}
                                                        onMouseMove={e => {
                                                            if (hoveredDaireID === daireID)
                                                                setTooltipPos({ x: e.clientX + 14, y: e.clientY - 10 });
                                                        }}
                                                        onMouseLeave={() => { setHoveredDaireID(null); setTooltipPos(null); }}
                                                        title=""
                                                    />
                                                );
                                            })}
                                            </div>
                                            {/* Etiket katmanı — zoom/pan dönüşümünün DIŞINDA, ekran pikseli cinsinden
                                                konumlanır; böylece yazı zoom ile büyüyüp küçülmez, nokta ile aynı
                                                sabit görünür boyutta kalır. Çakışan etiketler computeLabelLayout ile
                                                ayrıştırılır; noktadan uzaklaşanlara ince bir bağlantı çizgisi çizilir. */}
                                            <div style={{position:'absolute',inset:0,pointerEvents:'none',zIndex:22}}>
                                                {labelRenderData.some(l => l.needsConnector) && (
                                                    <svg style={{position:'absolute',inset:0,width:'100%',height:'100%',overflow:'visible'}}>
                                                        {labelRenderData.filter(l => l.needsConnector).map(l => (
                                                            <line key={l.daireID} x1={l.ax} y1={l.ay} x2={l.x + l.w / 2} y2={l.y + l.h / 2}
                                                                stroke={l.bg} strokeWidth={1.4} opacity={0.55} />
                                                        ))}
                                                    </svg>
                                                )}
                                                {labelRenderData.map(l => {
                                                    const isHoveredDot = hoveredDaireID === l.daireID;
                                                    return (
                                                        <div key={l.daireID}
                                                            style={{
                                                                position: 'absolute', left: l.x, top: l.y, width: l.w, height: l.h,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                background: isHoveredDot ? l.bg : 'rgba(255,255,255,0.92)',
                                                                color: isHoveredDot ? '#fff' : '#1e293b',
                                                                border: `1.4px solid ${l.bg}`,
                                                                borderRadius: 6, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap',
                                                                boxShadow: '0 1px 4px rgba(15,23,42,.18)',
                                                                pointerEvents: 'auto',
                                                                cursor: !readOnly ? (rectMode ? 'pointer' : 'grab') : (l.satista ? 'pointer' : 'default'),
                                                                transition: 'background .12s,color .12s',
                                                            }}
                                                            // Etikete basmak da tıpkı noktaya basmak gibi davransın: normal modda
                                                            // sürükleyip taşımayı, Alan Çiz modunda ise hedef seçimini başlatır.
                                                            onMouseDown={!readOnly ? e => handleDotMouseDown(e, l.daireID) : undefined}
                                                            onMouseEnter={e => { setHoveredDaireID(l.daireID); setTooltipPos({ x: e.clientX + 14, y: e.clientY - 10 }); }}
                                                            onMouseMove={e => { if (hoveredDaireID === l.daireID) setTooltipPos({ x: e.clientX + 14, y: e.clientY - 10 }); }}
                                                            onMouseLeave={() => { setHoveredDaireID(null); setTooltipPos(null); }}
                                                            onClick={() => { if (mouseMovedRef.current || rectMode) return; if (l.satista) openDetay(l.daireID); }}
                                                        >
                                                            {l.daireNo}
                                                            {/* Kaldırma düğmesi: etiketin İÇİNDE (çocuk) durur, dışına sibling olarak
                                                                konursa üzerine gelince üst öğenin onMouseLeave'i tetiklenip
                                                                düğme hemen kayboluyor, tıklama hiç ulaşmıyordu. */}
                                                            {!readOnly && isHoveredDot && (
                                                                <button
                                                                    onMouseDown={e => e.stopPropagation()}
                                                                    onClick={e => { e.stopPropagation(); removePlacement(l.daireID); }}
                                                                    title="Kaldır"
                                                                    style={{
                                                                        position: 'absolute', top: -7, right: -7,
                                                                        width: 15, height: 15, borderRadius: '50%', background: '#dc2626',
                                                                        color: '#fff', border: '1.5px solid #fff', fontSize: 9, fontWeight: 700,
                                                                        lineHeight: 1, cursor: 'pointer',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        boxShadow: '0 1px 3px rgba(0,0,0,.35)',
                                                                    }}
                                                                >✕</button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    </div>
                                    <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end'}}>
                                        <span style={{fontSize:11,color:'#94a3b8'}}>{Object.keys(placements).length} daire yerleştirildi</span>
                                    </div>
                                </div>
                                {!readOnly && <div className="pgv-save-bar">
                                    <span style={{fontSize:11,color:'#94a3b8'}}>{is3D ? 'Bu cepheye daireleri sürükle bırakla ekleyin.' : 'Daireleri sürükle bırakla ekleyin, üzerine gelerek kaldırabilirsiniz.'}</span>
                                    <button className="pgv-btn pgv-btn-primary" onClick={savePlacements} disabled={saving || (is3D && !activeCepheID)}>
                                        <i className="fas fa-save"></i>{saving ? 'Kaydediliyor...' : 'Yerleşimi Kaydet'}
                                    </button>
                                </div>}
                                </>}
                                {previewMode && is3D && (() => {
                                    const withImageCepheler = cepheListSorted.filter(c => c.gorselYolu);
                                    const currentPreviewCepheID = withImageCepheler[previewIndex]?.cepheID ?? null;
                                    return (
                                        <Gorunum3DPreview
                                            cepheler={cepheListSorted}
                                            daireler={selectedGorunum.daireler}
                                            daireData={daireler}
                                            apiBase={API_BASE}
                                            index={previewIndex}
                                            setIndex={setPreviewIndex}
                                            onDaireClick={openDetay}
                                            visibleDurumlar={visibleDurumlar}
                                            overlay={renderCepheOverlay(currentPreviewCepheID, id => {
                                                const idx = withImageCepheler.findIndex(c => c.cepheID === id);
                                                if (idx >= 0) setPreviewIndex(idx);
                                            }, !readOnly)}
                                        />
                                    );
                                })()}
                            </>
                        )}
                    </div>

                    {/* RIGHT PANEL — daire list (edit only) */}
                    {!readOnly && <div className="pgv-panel">
                        <div className="pgv-panel-head">
                            <i className="fas fa-home" style={{marginRight:6,color:'#cd6906'}}></i>
                            Daireler
                        </div>
                        <div className="pgv-panel-filter">
                            <select className="pgv-panel-select" value={filterPanelBlok} onChange={e => setFilterPanelBlok(e.target.value === '' ? '' : Number(e.target.value))}>
                                <option value="">Tüm Bloklar</option>
                                {bloklar.map(b => <option key={b.id} value={b.id}>{b.ad}</option>)}
                            </select>
                            <select className="pgv-panel-select" value={filterPanelKat} onChange={e => setFilterPanelKat(e.target.value === '' ? '' : Number(e.target.value))}>
                                <option value="">Tüm Katlar</option>
                                {katlar.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
                            </select>
                            <input className="pgv-panel-input" placeholder="Daire ara..." value={filterPanel} onChange={e => setFilterPanel(e.target.value)} />
                        </div>
                        <div className="pgv-panel-list">
                            {panelDaireler.map(d => {
                                const meta = durumMeta[d.daireDurumu] ?? { bg: '#94a3b8', border: '#64748b', text: '#fff' };
                                const onCanvas = !!placements[d.daireID];
                                const blok = bloklar.find(b => b.id === d.blokID)?.ad ?? '';
                                return (
                                    <div
                                        key={d.daireID}
                                        className={`pgv-panel-daire${onCanvas ? ' on-canvas' : ''}`}
                                        draggable
                                        onDragStart={e => {
                                            e.dataTransfer.setData('daireID', String(d.daireID));
                                            setDraggingDaireID(d.daireID);
                                        }}
                                        onDragEnd={() => setDraggingDaireID(null)}
                                    >
                                        <div className="pgv-panel-dot" style={{background:meta.bg}}/>
                                        <div style={{flex:1,minWidth:0}}>
                                            <div className="pgv-panel-daire-no">{d.daireNo}</div>
                                            {blok && <div style={{fontSize:10,color:'#94a3b8'}}>{blok}</div>}
                                        </div>
                                        {onCanvas && <div className="pgv-panel-placed">✓</div>}
                                    </div>
                                );
                            })}
                            {panelDaireler.length === 0 && (
                                <div style={{padding:20,textAlign:'center',color:'#94a3b8',fontSize:12}}>Daire bulunamadı</div>
                            )}
                        </div>
                    </div>}
                </div>
            </div>

            {/* ── DAİRE DETAY MODALI ── */}
            {detayDaire && (() => {
                const d = detayDaire;
                const blok    = bloklar.find(b => b.id === d.blokID)?.ad;
                const kat     = katlar.find(k => k.id === d.katID)?.ad;
                const cephe   = cepheler.find(c => c.id === d.cepheID)?.ad;
                const manzara = manzaralar.find(m => m.id === d.manzaraID)?.ad;
                const daireTipi = daireTipleri.find(dt => dt.id === d.daireTipID)?.ad;
                const meta    = durumMeta[d.daireDurumu] ?? { bg: '#94a3b8', border: '#64748b', text: '#fff' };
                const modalStyle: React.CSSProperties = modalPos
                    ? { position: 'fixed', left: modalPos.x, top: modalPos.y, zIndex: 100000, width: 520, maxWidth: 'calc(100vw - 24px)', background: '#fff', borderRadius: 14, boxShadow: '0 24px 80px rgba(15,23,42,.3)', fontFamily: "'Inter',sans-serif", overflow: 'hidden', userSelect: 'none' }
                    : { position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', zIndex: 100000, width: 520, maxWidth: 'calc(100vw - 24px)', background: '#fff', borderRadius: 14, boxShadow: '0 24px 80px rgba(15,23,42,.3)', fontFamily: "'Inter',sans-serif", overflow: 'hidden', userSelect: 'none' };

                const onDragStart = (e: React.MouseEvent) => {
                    e.preventDefault();
                    const rect = (e.currentTarget as HTMLElement).closest('[data-detay-modal]')!.getBoundingClientRect();
                    modalDragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top };
                    const onMove = (ev: MouseEvent) => {
                        if (!modalDragRef.current) return;
                        const dx = ev.clientX - modalDragRef.current.startX;
                        const dy = ev.clientY - modalDragRef.current.startY;
                        setModalPos({ x: modalDragRef.current.origX + dx, y: modalDragRef.current.origY + dy });
                    };
                    const onUp = () => {
                        modalDragRef.current = null;
                        window.removeEventListener('mousemove', onMove);
                        window.removeEventListener('mouseup', onUp);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                };

                return (
                    <>
                    {/* Hafif backdrop — tıklayınca kapatır ama modalı engellemez */}
                    <div style={{position:'fixed',inset:0,zIndex:99999}} onClick={() => { setDetayDaire(null); setSunumModal(false); }} />
                    <div data-detay-modal="" style={modalStyle} onClick={e => e.stopPropagation()}>
                            {/* Galeri */}
                            {galeriYukleniyor ? (
                                <div style={{height:200,background:'#0f172a',display:'flex',alignItems:'center',justifyContent:'center',color:'#94a3b8',gap:8}}>
                                    <i className="fas fa-spinner fa-spin"/>Görseller yükleniyor...
                                </div>
                            ) : galeriGorseller.length > 0 ? (
                                <div style={{position:'relative',background:'#0f172a',overflow:'hidden'}}>
                                    <img
                                        src={`${API_BASE}/api/SunumBelgeleri/download/${galeriGorseller[galeriSlide].sunumBelgeId}`}
                                        alt={galeriGorseller[galeriSlide].aciklama ?? ''}
                                        // contain: görsel kendi en-boy oranını korur (cover kırpıyordu)
                                        style={{width:'100%',height:220,objectFit:'contain',display:'block'}}
                                    />
                                    {galeriGorseller[galeriSlide].aciklama && (
                                        <div style={{position:'absolute',bottom:0,left:0,right:0,background:'linear-gradient(transparent,rgba(0,0,0,.65))',color:'#fff',padding:'18px 14px 8px',fontSize:12,fontWeight:600}}>
                                            {galeriGorseller[galeriSlide].aciklama}
                                        </div>
                                    )}
                                    {galeriGorseller.length > 1 && (<>
                                        <button
                                            onClick={() => setGaleriSlide(i => i <= 0 ? galeriGorseller.length - 1 : i - 1)}
                                            style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,.88)',border:'none',borderRadius:'50%',width:30,height:30,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#334155',boxShadow:'0 2px 8px rgba(0,0,0,.2)'}}>
                                            <i className="fas fa-chevron-left" style={{fontSize:11}}/>
                                        </button>
                                        <button
                                            onClick={() => setGaleriSlide(i => i >= galeriGorseller.length - 1 ? 0 : i + 1)}
                                            style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,.88)',border:'none',borderRadius:'50%',width:30,height:30,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#334155',boxShadow:'0 2px 8px rgba(0,0,0,.2)'}}>
                                            <i className="fas fa-chevron-right" style={{fontSize:11}}/>
                                        </button>
                                        <div style={{position:'absolute',top:8,right:8,background:'rgba(0,0,0,.45)',color:'#fff',fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:9999}}>
                                            {galeriSlide + 1} / {galeriGorseller.length}
                                        </div>
                                    </>)}
                                    {/* Thumbnail strip */}
                                    {galeriGorseller.length > 1 && (
                                        <div style={{display:'flex',gap:5,padding:'6px 8px',background:'rgba(0,0,0,.4)',overflowX:'auto',scrollbarWidth:'none'}}>
                                            {galeriGorseller.map((foto, i) => (
                                                <div key={foto.sunumBelgeId} onClick={() => setGaleriSlide(i)}
                                                    style={{width:50,height:38,flexShrink:0,borderRadius:5,overflow:'hidden',cursor:'pointer',border: i === galeriSlide ? '2px solid #cd6906' : '2px solid transparent',opacity: i === galeriSlide ? 1 : 0.55,transition:'all .15s'}}>
                                                    <img src={`${API_BASE}/api/SunumBelgeleri/download/${foto.sunumBelgeId}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            {/* Header — sürükleme tutamacı */}
                            <div onMouseDown={onDragStart}
                                style={{padding:'18px 20px 14px',borderBottom:'1px solid #e2e8f0',display:'flex',alignItems:'flex-start',justifyContent:'space-between',cursor:'grab',background:'#fafbfc'}}>
                                <div>
                                    <div style={{fontSize:11,fontWeight:700,color:'#94a3b8',letterSpacing:'.4px',marginBottom:4,display:'flex',alignItems:'center',gap:6}}>
                                        <i className="fas fa-grip-vertical" style={{opacity:.35,fontSize:10}}/>DAİRE BİLGİSİ</div>
                                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                                        <span style={{fontSize:24,fontWeight:800,color:'#0f172a',letterSpacing:'-0.03em'}}>Daire {d.daireNo}</span>
                                        <span style={{padding:'3px 10px',borderRadius:20,fontSize:11.5,fontWeight:700,background:meta.bg,color:meta.text}}>
                                            {d.daireDurumu}
                                        </span>
                                    </div>
                                </div>
                                <button onClick={() => { setDetayDaire(null); setSunumModal(false); }}
                                    style={{width:30,height:30,border:'none',background:'#f1f5f9',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#64748b',fontSize:14,flexShrink:0}}>✕</button>
                            </div>
                            {/* Info Grid */}
                            <div style={{padding:'16px 20px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px 16px'}}>
                                {[
                                    { label:'Daire Tipi', val: daireTipi },
                                    { label:'Blok',     val: blok },
                                    { label:'Kat',      val: kat },
                                    { label:'Cephe',    val: cephe },
                                    { label:'Manzara',  val: manzara },
                                    { label:'Net m²',   val: d.netM2 ? `${d.netM2} m²` : null },
                                    { label:'Brüt m²',  val: d.brutM2 ? `${d.brutM2} m²` : null },
                                ].map(({ label, val }) => val ? (
                                    <div key={label}>
                                        <div style={{fontSize:10.5,fontWeight:700,color:'#94a3b8',letterSpacing:'.3px',marginBottom:2}}>{label.toUpperCase()}</div>
                                        <div style={{fontSize:13.5,fontWeight:600,color:'#1e293b'}}>{val}</div>
                                    </div>
                                ) : null)}
                            </div>
                            {d.listeFiyati && (
                                <div style={{padding:'0 20px 14px'}}>
                                    <div style={{background:'#f8fafc',borderRadius:10,padding:'10px 14px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                                        <span style={{fontSize:11.5,fontWeight:700,color:'#64748b'}}>FİYAT</span>
                                        <span style={{fontSize:18,fontWeight:800,color:'#0f172a',letterSpacing:'-0.02em'}}>{formatMoney(d.listeFiyati)} ₺</span>
                                    </div>
                                </div>
                            )}
                            {/* Aksiyon Butonları */}
                            {d.daireDurumu === 'Satışta' && (
                                <div style={{padding:'0 20px 18px',display:'flex',gap:8}}>
                                    <button onClick={openSunumaEkle}
                                        style={{flex:1,padding:'11px 0',border:'none',borderRadius:10,background:'#cd6906',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                                        <i className="fas fa-plus" style={{fontSize:12}}/>
                                        Sunuma Ekle
                                    </button>
                                    <button onClick={() => { setDetayDaire(null); setSunumModal(false); onClose(); navigate('/satis-sozlesmesi', { state: { selectedDaire: d } }); }}
                                        style={{flex:1,padding:'11px 0',border:'none',borderRadius:10,background:'#1e293b',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                                        <i className="fas fa-file-contract" style={{fontSize:12}}/>
                                        Sözleşme Yap
                                    </button>
                                </div>
                            )}

                            {/* SunumaEkle alt-paneli */}
                            {sunumModal && (
                                <div style={{borderTop:'1px solid #e2e8f0'}}>
                                    <div style={{padding:'12px 20px 8px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                                        <span style={{fontSize:12,fontWeight:700,color:'#475569'}}>
                                            <i className="fas fa-list" style={{marginRight:6,color:'#cd6906'}}/>
                                            Aktif Sunumlar
                                            <span style={{fontSize:11,fontWeight:400,color:'#94a3b8',marginLeft:6}}>Taslak / Devam Ediyor</span>
                                        </span>
                                        <button onClick={() => setSunumModal(false)}
                                            style={{border:'none',background:'none',cursor:'pointer',color:'#94a3b8',fontSize:15,lineHeight:1}}>✕</button>
                                    </div>
                                    <div style={{maxHeight:220,overflowY:'auto',padding:'0 12px 12px'}}>
                                        {sunumYukleniyor ? (
                                            <div style={{padding:'20px',textAlign:'center',color:'#94a3b8',fontSize:13}}>
                                                <i className="fas fa-spinner fa-spin" style={{marginRight:6}}/>Yükleniyor...
                                            </div>
                                        ) : sunumlar.length === 0 ? (
                                            <div style={{padding:'20px',textAlign:'center',color:'#94a3b8',fontSize:13}}>
                                                Taslak veya devam eden sunum bulunamadı.
                                            </div>
                                        ) : sunumlar.map(s => {
                                            const ekli = sunumEklendi.has(s.sunumID);
                                            const isEkleniyor = sunumEkleniyor === s.sunumID;
                                            const durumRenk: Record<string,{bg:string;col:string}> = {
                                                'Taslak': {bg:'#f1f5f9',col:'#475569'},
                                                'Devam Ediyor': {bg:'#eff6ff',col:'#1d4ed8'},
                                            };
                                            const dk = durumRenk[s.durum] ?? {bg:'#f1f5f9',col:'#475569'};
                                            return (
                                                <div key={s.sunumID} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',borderRadius:9,background:'#fff',border:'1px solid #e2e8f0',marginBottom:6}}>
                                                    <div style={{flex:1,minWidth:0}}>
                                                        <div style={{fontSize:12.5,fontWeight:700,color:'#1e293b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                                                            {s.baslik || s.cariAdSoyad || s.takipNo}
                                                        </div>
                                                        <div style={{fontSize:11,color:'#64748b',marginTop:1,display:'flex',alignItems:'center',gap:6}}>
                                                            {s.cariAdSoyad && <span>{s.cariAdSoyad}</span>}
                                                            <span style={{padding:'1px 7px',borderRadius:10,fontSize:10,fontWeight:600,background:dk.bg,color:dk.col}}>{s.durum}</span>
                                                            <span style={{color:'#94a3b8'}}>{s.daireler.length} daire</span>
                                                        </div>
                                                    </div>
                                                    {ekli ? (
                                                        <span style={{padding:'4px 10px',borderRadius:8,background:'#f0fdf4',color:'#15803d',fontSize:11,fontWeight:700,flexShrink:0}}>
                                                            <i className="fas fa-check" style={{marginRight:4}}/>Mevcut
                                                        </span>
                                                    ) : (
                                                        <button onClick={() => handleSunumaEkle(s.sunumID)} disabled={isEkleniyor}
                                                            style={{padding:'5px 12px',borderRadius:8,border:'none',background:'#cd6906',color:'#fff',fontSize:11.5,fontWeight:700,cursor:isEkleniyor?'not-allowed':'pointer',flexShrink:0,opacity:isEkleniyor?.7:1,display:'flex',alignItems:'center',gap:4}}>
                                                            {isEkleniyor ? <i className="fas fa-spinner fa-spin"/> : <i className="fas fa-plus"/>}
                                                            Ekle
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                    </div>
                    </>
                );
            })()}

            {/* Tooltip */}
            {hoveredDaireID !== null && tooltipPos && (() => {
                const d = getDaire(hoveredDaireID);
                if (!d) return null;
                const meta = durumMeta[d.daireDurumu] ?? { bg: '#94a3b8', border: '#64748b', text: '#fff' };
                const blok = bloklar.find(b => b.id === d.blokID)?.ad;
                const kat = katlar.find(k => k.id === d.katID)?.ad;
                const cephe = cepheler.find(c => c.id === d.cepheID)?.ad;
                const manzara = manzaralar.find(m => m.id === d.manzaraID)?.ad;
                const daireTipi = daireTipleri.find(dt => dt.id === d.daireTipID)?.ad;
                return (
                    <div className="pgv-tooltip" style={{left: tooltipPos.x, top: tooltipPos.y}}>
                        <div className="pgv-tooltip-title">{d.daireNo}</div>
                        {daireTipi && <div className="pgv-tooltip-row"><span>Daire Tipi</span><span>{daireTipi}</span></div>}
                        {blok && <div className="pgv-tooltip-row"><span>Blok</span><span>{blok}</span></div>}
                        {kat && <div className="pgv-tooltip-row"><span>Kat</span><span>{kat}</span></div>}
                        {cephe && <div className="pgv-tooltip-row"><span>Cephe</span><span>{cephe}</span></div>}
                        {manzara && <div className="pgv-tooltip-row"><span>Manzara</span><span>{manzara}</span></div>}
                        {d.netM2 && <div className="pgv-tooltip-row"><span>Net m²</span><span>{d.netM2}</span></div>}
                        {d.brutM2 && <div className="pgv-tooltip-row"><span>Brüt m²</span><span>{d.brutM2}</span></div>}
                        {d.listeFiyati && <div className="pgv-tooltip-row"><span>Fiyat</span><span>{formatMoney(d.listeFiyati)}</span></div>}
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                            <span className="pgv-tooltip-badge" style={{background:meta.bg, color: meta.text}}>
                                {d.daireDurumu}
                            </span>
                            {d.daireDurumu === 'Satışta' && (
                                <span style={{fontSize:10,color:'#94a3b8',opacity:.8}}>
                                    <i className="fas fa-mouse-pointer" style={{marginRight:3}}/>tıkla → detay
                                </span>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

// ── 3D Görünüm — sürükle-döndür önizlemesi ───────────────────────────────────
// Teknik: aktif ve komşu (önceki/sonraki) cephe fotoğrafları AYNI ortak "t" ilerleme
// değerinden türetilen translateX ile konumlanır; birleşim (dikiş) kenarları her iki
// panelde de bu ortak değere bağlı olduğundan matematiksel olarak HER ZAMAN çakışır —
// aralarında boşluk oluşması mümkün değildir. Her panelin döndürme ekseni (transform-origin)
// tam da bu dikiş kenarına sabitlendiğinden, dikiş her zaman tam yükseklikte kalır; panelin
// diğer (dikişten uzak) kenarı ise perspektifle küçülüp uzaklaşarak 3D döndürme hissi verir.
interface Gorunum3DPreviewProps {
    cepheler: Cephe[];
    daireler: GorunumDaire[];
    daireData: Daire[];
    apiBase: string;
    index: number;
    setIndex: (i: number) => void;
    onDaireClick: (daireID: number) => void;
    overlay?: React.ReactNode;
    visibleDurumlar: Set<string>;
}

// Toplam cephe sayısından bağımsız, sabit bir "köşe açısı" — bir komşu cepheye tam geçiş
// bu kadarlık bir fare sürüklemesi (panel genişliğinin bir kesri) gerektirir.
const CORNER_ANGLE = 45;

const Gorunum3DPreview: React.FC<Gorunum3DPreviewProps> = ({ cepheler, daireler, daireData, apiBase, index, setIndex, onDaireClick, overlay, visibleDurumlar }) => {
    const arRef = useRef<HTMLDivElement>(null);
    const [panelWidth, setPanelWidth] = useState(600);
    const [dragT, setDragT] = useState(0); // -1..1 (komşuya geçiş ilerlemesi)
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef<{ startX: number; rawT: number } | null>(null);

    const withImage = cepheler.filter(c => c.gorselYolu);
    const N = withImage.length;
    const safeIndex = Math.min(Math.max(index, 0), Math.max(0, N - 1));
    const perspective = Math.max(900, panelWidth * 1.6);

    useEffect(() => {
        const el = arRef.current;
        if (!el) return;
        const measure = () => setPanelWidth(el.getBoundingClientRect().width || 600);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const wrap = (i: number) => N > 0 ? ((i % N) + N) % N : 0;
    const goTo = (i: number) => { if (N > 0) setIndex(wrap(i)); };

    const onPointerDown = (e: React.MouseEvent) => {
        if (N <= 1) return;
        dragRef.current = { startX: e.clientX, rawT: 0 };
        setIsDragging(true);
    };
    const onPointerMove = (e: React.MouseEvent) => {
        if (!dragRef.current) return;
        const dx = e.clientX - dragRef.current.startX;
        const raw = -dx / panelWidth; // sağa sürükle (dx>0) → önceki (t negatif); sola sürükle → sonraki (t pozitif)
        dragRef.current.rawT = raw;
        setDragT(Math.max(-1, Math.min(1, raw)));
    };
    const endDrag = () => {
        if (!dragRef.current) return;
        const steps = Math.round(dragRef.current.rawT);
        dragRef.current = null;
        setIsDragging(false);
        setDragT(0);
        if (steps !== 0) goTo(safeIndex + steps);
    };

    if (N === 0) {
        return (
            <div className="pgv-canvas-wrap">
                <div className="pgv-canvas-empty" style={{position:'static'}}>
                    <i className="fas fa-cube" style={{fontSize:32,color:'#d1d5db'}}></i>
                    <span style={{fontSize:12,color:'#94a3b8'}}>Önizleme için en az bir cepheye fotoğraf yükleyin</span>
                </div>
            </div>
        );
    }

    const getDaire = (id: number) => daireData.find(d => d.daireID === id);

    const renderMarkers = (c: Cephe) => daireler.filter(d => d.cepheID === c.cepheID).map(d => {
        const daire = getDaire(d.daireID);
        if (!daire) return null;
        if (!visibleDurumlar.has(getDurumKey(daire.daireDurumu))) return null;
        const meta = durumMeta[daire.daireDurumu] ?? { bg: '#94a3b8', border: '#64748b', text: '#fff' };
        return (
            <div key={d.gorunumDaireID}
                className={`pgv-daire-dot${daire.daireDurumu === 'Satışta' ? ' satista' : ''}`}
                style={{ left: `${d.xYuzde}%`, top: `${d.yYuzde}%`, background: meta.bg, borderColor: meta.border }}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                    e.stopPropagation();
                    if (daire.daireDurumu === 'Satışta') onDaireClick(d.daireID);
                }}
            >
                <span style={{fontSize:7,fontWeight:800,color:meta.text,pointerEvents:'none',textAlign:'center',lineHeight:1.1}}>
                    {daire.daireNo.length <= 4 ? daire.daireNo : daire.daireNo.slice(0,4)}
                </span>
            </div>
        );
    });

    const t = dragT;
    const tNext = Math.max(0, Math.min(1, t));
    const tPrev = Math.max(-1, Math.min(0, t));
    const txCommon = -t * panelWidth;
    const trans = isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.22,1,0.36,1)';
    // prev/next sarmalayıcıları görsel olarak neredeyse tamamen dönük/gizli olsa bile
    // DOM'da tam boyutlu (100%x100%) birer kutu olarak durur ve normal şartlarda üstte
    // kalarak "current" içindeki marker'lara giden tıklamaları önceden yakalar.
    // Sadece o an "aktif/etkileşimli" olan panel pointer-events alsın.
    const cephePanelStyle = (interactive: boolean): React.CSSProperties => ({
        position:'absolute', top:0, left:0, width:'100%', height:'100%', overflow:'hidden',
        transformStyle:'preserve-3d', perspective: `${perspective}px`,
        pointerEvents: interactive ? 'auto' : 'none',
    });

    const current = withImage[safeIndex];
    const nextCephe = N > 1 ? withImage[wrap(safeIndex + 1)] : null;
    const prevCephe = N > 1 ? withImage[wrap(safeIndex - 1)] : null;

    return (
        <div className="pgv-canvas-wrap">
            <div className="pgv-canvas-frame">
                <div className="pgv-canvas-ar has-bg" ref={arRef}
                    style={{cursor: N > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default', userSelect:'none', perspective: `${perspective}px`}}
                    onMouseDown={onPointerDown}
                    onMouseMove={onPointerMove}
                    onMouseUp={endDrag}
                    onMouseLeave={endDrag}
                >
                    {/* Önceki cephe — soldan içeri kayar, dikiş (sağ kenarı) current'ın sol kenarıyla çakışır */}
                    {prevCephe && prevCephe.cepheID !== current.cepheID && (
                        <div style={cephePanelStyle(false)}>
                            <div style={{
                                position:'absolute', inset:0, transformOrigin:'100% 50%', backfaceVisibility:'hidden',
                                transform:`translateX(${txCommon - panelWidth}px) rotateY(${-(1 + tPrev) * CORNER_ANGLE}deg)`,
                                transition: trans,
                            }}>
                                <img src={`${apiBase}/api/ProjeGorunumleri/gorsel/${prevCephe.gorselYolu}`} alt={prevCephe.cepheAdi} draggable={false}
                                    style={{ width: '100%', height: '100%', objectFit: 'fill', display:'block', pointerEvents:'none' }} />
                                {renderMarkers(prevCephe)}
                            </div>
                        </div>
                    )}

                    {/* Şimdiki cephe — dikiş kenarı sürükleme yönüne göre sağda ya da solda sabit kalır */}
                    <div style={cephePanelStyle(true)}>
                        <div style={{
                            position:'absolute', inset:0, transformOrigin: t >= 0 ? '100% 50%' : '0% 50%', backfaceVisibility:'hidden',
                            transform:`translateX(${txCommon}px) rotateY(${-t * CORNER_ANGLE}deg)`,
                            transition: trans,
                        }}>
                            <img src={`${apiBase}/api/ProjeGorunumleri/gorsel/${current.gorselYolu}`} alt={current.cepheAdi} draggable={false}
                                style={{ width: '100%', height: '100%', objectFit: 'fill', display:'block', pointerEvents:'none' }} />
                            {renderMarkers(current)}
                        </div>
                    </div>

                    {/* Sonraki cephe — sağdan içeri kayar, dikiş (sol kenarı) current'ın sağ kenarıyla çakışır */}
                    {nextCephe && nextCephe.cepheID !== current.cepheID && (
                        <div style={cephePanelStyle(false)}>
                            <div style={{
                                position:'absolute', inset:0, transformOrigin:'0% 50%', backfaceVisibility:'hidden',
                                transform:`translateX(${txCommon + panelWidth}px) rotateY(${(1 - tNext) * CORNER_ANGLE}deg)`,
                                transition: trans,
                            }}>
                                <img src={`${apiBase}/api/ProjeGorunumleri/gorsel/${nextCephe.gorselYolu}`} alt={nextCephe.cepheAdi} draggable={false}
                                    style={{ width: '100%', height: '100%', objectFit: 'fill', display:'block', pointerEvents:'none' }} />
                                {renderMarkers(nextCephe)}
                            </div>
                        </div>
                    )}

                    {/* Düz (döndürülmeyen) kontroller */}
                    {overlay}
                    {N > 1 && <>
                        <button onClick={() => goTo(safeIndex - 1)}
                            style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,.88)',border:'none',borderRadius:'50%',width:32,height:32,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#334155',boxShadow:'0 2px 8px rgba(0,0,0,.2)',zIndex:40}}>
                            <i className="fas fa-chevron-left" style={{fontSize:12}}/>
                        </button>
                        <button onClick={() => goTo(safeIndex + 1)}
                            style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,.88)',border:'none',borderRadius:'50%',width:32,height:32,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#334155',boxShadow:'0 2px 8px rgba(0,0,0,.2)',zIndex:40}}>
                            <i className="fas fa-chevron-right" style={{fontSize:12}}/>
                        </button>
                        <div style={{position:'absolute',bottom:10,left:'50%',transform:'translateX(-50%)',display:'flex',gap:6,zIndex:40}}>
                            {withImage.map((c, i) => (
                                <button key={c.cepheID} onClick={() => goTo(i)}
                                    style={{width:8,height:8,borderRadius:'50%',border:'none',cursor:'pointer',background: i === safeIndex ? '#cd6906' : 'rgba(255,255,255,.6)',padding:0}} />
                            ))}
                        </div>
                    </>}
                </div>
            </div>
            <div style={{textAlign:'center',fontSize:11,color:'#94a3b8'}}>
                <i className="fas fa-arrows-left-right" style={{marginRight:6}}/>Fareyle sağa/sola sürükleyerek veya oklarla cepheler arasında gezinin
            </div>
        </div>
    );
};

export default ProjeGorunumleri;
